import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { profileAPI, companyAPI, mastersAPI, userMastersAPI, usersAPI as usersApiHook } from '../../api';
import { useTheme, THEME_LABELS, THEME_DEFAULTS } from '../../context/ThemeContext';
import toast from 'react-hot-toast';
import styles from './Setup.module.css';
import RolePermissionsPage from './RolePermissionsPage';
import EmailConfigPage from './EmailConfigPage';

// ─────────────────────────────────────────────────────────────
// DEMO DATA — User Master
// ─────────────────────────────────────────────────────────────
const DESIGNATIONS = [
  { id:1,  name:'Assistant General Manager', org_level:'L3' },
  { id:2,  name:'Assistant Manager',         org_level:'L4' },
  { id:3,  name:'Assistant Supervisor',      org_level:'L5' },
  { id:4,  name:'CEO',                       org_level:'L1' },
  { id:5,  name:'Chairman',                  org_level:'L0' },
  { id:6,  name:'Chief Executive Officer',   org_level:'L1' },
  { id:7,  name:'Chief Financial Officer',   org_level:'L2' },
  { id:8,  name:'Chief Information Officer', org_level:'L2' },
  { id:9,  name:'Consultant',                org_level:'L4' },
  { id:10, name:'Corporate Support',         org_level:'L5' },
  { id:11, name:'Deputy Manager',            org_level:'L4' },
  { id:12, name:'Director',                  org_level:'L2' },
  { id:13, name:'Engineer',                  org_level:'L5' },
  { id:14, name:'Executive',                 org_level:'L5' },
  { id:15, name:'General Manager',           org_level:'L3' },
  { id:16, name:'Graduate Engineer Trainee', org_level:'L6' },
  { id:17, name:'Head of Department',        org_level:'L3' },
  { id:18, name:'Junior Engineer',           org_level:'L6' },
  { id:19, name:'Manager',                   org_level:'L4' },
  { id:20, name:'Managing Director',         org_level:'L1' },
  { id:21, name:'President',                 org_level:'L1' },
  { id:22, name:'Production Manager',        org_level:'L3' },
  { id:23, name:'Project Manager',           org_level:'L3' },
  { id:24, name:'Quality Manager',           org_level:'L3' },
  { id:25, name:'Safety Officer',            org_level:'L5' },
  { id:26, name:'Senior Engineer',           org_level:'L4' },
  { id:27, name:'Senior Executive',          org_level:'L4' },
  { id:28, name:'Senior Manager',            org_level:'L3' },
  { id:29, name:'Supervisor',                org_level:'L5' },
  { id:30, name:'Team Leader',               org_level:'L4' },
  { id:31, name:'Technical Head',            org_level:'L3' },
  { id:32, name:'Technician',                org_level:'L6' },
  { id:33, name:'VP Engineering',            org_level:'L2' },
  { id:34, name:'VP Finance',                org_level:'L2' },
  { id:35, name:'VP Operations',             org_level:'L2' },
  { id:36, name:'VP Sales',                  org_level:'L2' },
  { id:37, name:'Warehouse Manager',         org_level:'L3' },
  { id:38, name:'Works Manager',             org_level:'L3' },
  { id:39, name:'Zone Manager',              org_level:'L3' },
  { id:40, name:'Zonal Head',                org_level:'L2' },
  { id:41, name:'Area Manager',              org_level:'L3' },
  { id:42, name:'Branch Manager',            org_level:'L3' },
  { id:43, name:'Business Analyst',          org_level:'L4' },
  { id:44, name:'Data Analyst',              org_level:'L5' },
  { id:45, name:'IT Manager',                org_level:'L3' },
  { id:46, name:'HR Manager',                org_level:'L3' },
  { id:47, name:'Finance Manager',           org_level:'L3' },
];

const ROLES = [
  { id:1, name:'Employee',  total_users:352, description:'Standard employee access' },
  { id:2, name:'Employee2', total_users:1,   description:'Secondary employee role'  },
  { id:3, name:'Admin',     total_users:5,   description:'Full system administrator' },
  { id:4, name:'Manager',   total_users:28,  description:'Department manager access' },
];

const USERS = [
  { id:1,  name:'Abhijeet Rajpure',      designation:'Engineer',                   department:'PPC',                    functional_area:'Mfg',  location:'Chakan 2, Indospace',          reporting_manager:'Satish Ghongade',     email:'baijajwebsites@gmail.com',            mobile:'+91 8766404843', active:false },
  { id:2,  name:'Abhijit Kale',          designation:'Deputy Manager',             department:'Management',             functional_area:'Mgmt', location:'Chakan 1, Mahalunge',          reporting_manager:'',                    email:'abhijit.kale@starengineers.in',      mobile:'+91 8605823434', active:true  },
  { id:3,  name:'Abhishek Nigam',        designation:'Graduate Engineer Trainee',  department:'R&D',                    functional_area:'R&D',  location:'R&D Centre, Talawade',         reporting_manager:'Shirish Bankar',      email:'abhishek.nigam@starengineers.in',    mobile:'+91 7558544336', active:false },
  { id:4,  name:'Abhishek Mahajan',      designation:'Senior Engineer',            department:'Customer Care',          functional_area:'Svc',  location:'Chakan 1, Mahalunge',          reporting_manager:'Wilson Gudinho',      email:'customercare1@starengineers.in',     mobile:'9893067133',     active:true  },
  { id:5,  name:'Abhishek Mishra',       designation:'Engineer',                   department:'NPD',                    functional_area:'R&D',  location:'R&D Centre, Talawade',         reporting_manager:'Akash Pratap Singh',  email:'npd1@starengineers.in',              mobile:'9764021907',     active:true  },
  { id:6,  name:'Abhishek Pachpol',      designation:'Senior Executive',           department:'Information Technology', functional_area:'IT',   location:'Corporate Office, Chinchwad',  reporting_manager:'Amol Mahajan',        email:'abhishek@iworkkz.com',               mobile:'+91 9922722582', active:true  },
  { id:7,  name:'Abhishek Pratap Singh', designation:'Production Manager',         department:'Production',             functional_area:'Mfg',  location:'Chakan 2, Indospace',          reporting_manager:'Manoj Gupta',         email:'abhishek.singh@starengineers.in',    mobile:'9871432699',     active:false },
  { id:8,  name:'Aditya Khandelwal',     designation:'Chief Financial Officer',    department:'Accounts',               functional_area:'Fin',  location:'Corporate Office, Chinchwad',  reporting_manager:'Neeraj Malik',        email:'aditya.khandelwal@starengineers.in', mobile:'+91 8123848645', active:true  },
  { id:9,  name:'Aditya Gamre',          designation:'Executive',                  department:'Accounts',               functional_area:'Fin',  location:'Corporate Office, Chinchwad',  reporting_manager:'Prakash Mahajan',     email:'star@starengineers.in',              mobile:'+91 9049786599', active:false },
  { id:10, name:'Administrator User',    designation:'System Admin',               department:'IT',                     functional_area:'IT',   location:'Corporate Office, Chinchwad',  reporting_manager:'',                    email:'iworkkzadmin@starengineers.in',      mobile:'8793057134',     active:true  },
];

const SUCCESSIONS = [
  { id:1, separated:'Amit Kumar - Bhatia', new_emp:'Satyawan - Bhosale', remark:'Replace', created_by:'Kapil', created_date:'2024-08-08 10:48:55' },
];

const AUTH_LEVELS = [
  { id:1,  code:'0', name:'Created By', document:'Purchase',        location:'Chakan 2, Indospace'         },
  { id:2,  code:'0', name:'Created By', document:'Purchase',        location:'Corporate Office, Chinchwad' },
  { id:3,  code:'0', name:'Created By', document:'Purchase',        location:'R&D Centre, Talawade'        },
  { id:4,  code:'0', name:'Created By', document:'Purchase',        location:'Chakan 1, Mahalunge'         },
  { id:5,  code:'0', name:'Created By', document:'Purchase',        location:'Mallur, Bengaluru'           },
  { id:6,  code:'0', name:'Created By', document:'Purchase',        location:'Kadi, Gujarat'               },
  { id:7,  code:'0', name:'Created By', document:'Purchase',        location:'Manesar'                     },
  { id:8,  code:'0', name:'Created By', document:'Purchase',        location:'Walunj, Aurangabad'          },
  { id:9,  code:'0', name:'Created By', document:'Purchase',        location:'Vinh Phuc, Vietnam'          },
  { id:10, code:'0', name:'Created By', document:'Business Travel', location:'Corporate Office, Chinchwad' },
  { id:11, code:'0', name:'Created By', document:'Business Travel', location:'Manesar'                     },
  { id:12, code:'0', name:'Created By', document:'Business Travel', location:'Chakan 1, Mahalunge'         },
  { id:13, code:'1', name:'Approver 1', document:'Purchase',        location:'Chakan 2, Indospace'         },
  { id:14, code:'1', name:'Approver 1', document:'Purchase',        location:'Corporate Office, Chinchwad' },
  { id:15, code:'2', name:'Approver 2', document:'Maintenance',     location:'Chakan 2, Indospace'         },
];

// ─────────────────────────────────────────────────────────────
// DEMO DATA — Company Master
// ─────────────────────────────────────────────────────────────
const DEMO_LOCATIONS = [
  { id:1,  location_code:'1',        erp_location_code:'1',        attendance_machine_code:'1',        hrms_location_code:'1',        substitute_location_code:'1',        location_name:'Vinh Phuc, Vietnam',          address:'Bình Xuyên IP, Son Loi Commune, Bình Xuyên District', is_active:true },
  { id:2,  location_code:'32565',    erp_location_code:null,       attendance_machine_code:null,       hrms_location_code:null,       substitute_location_code:null,       location_name:'Plant 3, Wakad, Pune',        address:'Sector 34, Wakad Bridge Road- 411033', is_active:true },
  { id:3,  location_code:'343',      erp_location_code:null,       attendance_machine_code:null,       hrms_location_code:null,       substitute_location_code:null,       location_name:'NewYork',                     address:'Newyork lane 2', is_active:true },
  { id:4,  location_code:'344',      erp_location_code:null,       attendance_machine_code:null,       hrms_location_code:null,       substitute_location_code:null,       location_name:'ABC, Pune',                   address:'Test', is_active:true },
  { id:5,  location_code:'5',        erp_location_code:null,       attendance_machine_code:null,       hrms_location_code:null,       substitute_location_code:null,       location_name:'Kadi, Gujarat',               address:'3T WAREHOUSE VITTALPUR,DIST-AHEMDABAD.', is_active:true },
  { id:6,  location_code:'6',        erp_location_code:null,       attendance_machine_code:null,       hrms_location_code:null,       substitute_location_code:null,       location_name:'Manesar',                     address:'PANCHGAON TAURU ROAD,VILLAGE BACHANIKI', is_active:true },
  { id:7,  location_code:'BLRE2324', erp_location_code:null,       attendance_machine_code:null,       hrms_location_code:null,       substitute_location_code:null,       location_name:'Mallur, Bengaluru',           address:'PLOT.NO.211P TO 213P,PHASE III,NARSAPURA INDUSTRIAL AREA,KOLAR DISTRICT,', is_active:true },
  { id:8,  location_code:'CH012324', erp_location_code:'CH012324', attendance_machine_code:'CH012324', hrms_location_code:'CH012324', substitute_location_code:'CH012324', location_name:'Chakan 1, Mahalunge',         address:'PLOT NO. A-32, PHASE 1, SUZILON ROAD,MIDC, CHAKAN, MAHALUNGE, KHED', is_active:true },
  { id:9,  location_code:'CH022324', erp_location_code:'CH022324', attendance_machine_code:'CH022324', hrms_location_code:'CH022324', substitute_location_code:'CH022324', location_name:'Chakan 2, Indospace',         address:'BUILDING NO. B-7, UNIT NO. E, GAT NO. 428,VILLAGE MAHALUNGE, IndoSpace Industrial Park,OFF. CHAKAN TALEGAON ROAD, TALUKA KHED', is_active:true },
  { id:10, location_code:'CHWD2324', erp_location_code:'CHWD2324', attendance_machine_code:'CHWD2324', hrms_location_code:'CHWD2324', substitute_location_code:'CHWD2324', location_name:'Corporate Office, Chinchwad', address:'54/2, D-II BLOCK, MIDC, CHINCHWAD', is_active:true },
  { id:11, location_code:'RD2324',   erp_location_code:null,       attendance_machine_code:null,       hrms_location_code:null,       substitute_location_code:null,       location_name:'R&D Centre, Talawade',        address:'Talawade IT Park, Pune', is_active:true },
  { id:12, location_code:'MNR2324',  erp_location_code:null,       attendance_machine_code:null,       hrms_location_code:null,       substitute_location_code:null,       location_name:'Mallur Bengaluru 2',          address:'Phase IV, Kolar District', is_active:true },
];
const DEMO_DEPTS = [
  { id:1, name:'Accounts',               code:'ACC', description:'', is_active:true },
  { id:2, name:'Customer Care',          code:'CC',  description:'', is_active:true },
  { id:3, name:'Human Resources',        code:'HR',  description:'', is_active:true },
  { id:4, name:'Information Technology', code:'IT',  description:'', is_active:true },
  { id:5, name:'Management',             code:'MGT', description:'', is_active:true },
  { id:6, name:'NPD',                    code:'NPD', description:'', is_active:true },
  { id:7, name:'PPC',                    code:'PPC', description:'', is_active:true },
  { id:8, name:'Production',             code:'PRD', description:'', is_active:true },
  { id:9, name:'R&D',                    code:'RND', description:'', is_active:true },
];
const DEMO_FA = [
  { id:1, name:'Finance',               code:'FIN', description:'', is_active:true },
  { id:2, name:'Information Technology',code:'IT',  description:'', is_active:true },
  { id:3, name:'Manufacturing',         code:'MFG', description:'', is_active:true },
  { id:4, name:'R&D',                   code:'R&D', description:'', is_active:true },
  { id:5, name:'Sales',                 code:'SAL', description:'', is_active:true },
  { id:6, name:'Service',               code:'SVC', description:'', is_active:true },
];
const DEMO_CURR = [
  { id:1, code:'INR', name:'Indian Rupee',  symbol:'₹', decimal_separator:'.', thousand_separator:',', currency_placement:'before', is_default:true,  is_active:true },
  { id:2, code:'USD', name:'US Dollar',     symbol:'$', decimal_separator:'.', thousand_separator:',', currency_placement:'before', is_default:false, is_active:true },
  { id:3, code:'EUR', name:'Euro',          symbol:'€', decimal_separator:',', thousand_separator:'.', currency_placement:'before', is_default:false, is_active:true },
  { id:4, code:'GBP', name:'British Pound', symbol:'£', decimal_separator:'.', thousand_separator:',', currency_placement:'before', is_default:false, is_active:true },
];
const DEMO_EVTLOC = [
  { id:1, name:'Conference Hall A', code:'CHA', address:'Corporate Office, Chinchwad', capacity:100, is_active:true },
  { id:2, name:'Training Room 1',   code:'TR1', address:'Chakan 1, Mahalunge',         capacity:30,  is_active:true },
];
const DEMO_FY = [
  { id:1, label:'FY 2024-25', start_date:'2024-04-01', end_date:'2025-03-31', is_current:true,  is_active:true },
  { id:2, label:'FY 2023-24', start_date:'2023-04-01', end_date:'2024-03-31', is_current:false, is_active:true },
  { id:3, label:'FY 2022-23', start_date:'2022-04-01', end_date:'2023-03-31', is_current:false, is_active:true },
];
const DEMO_ORGLEVELS = [
  { id:1, code:'L0', name:'Board / Chairman',     sort_order:0, description:'', is_active:true },
  { id:2, code:'L1', name:'C-Suite',              sort_order:1, description:'', is_active:true },
  { id:3, code:'L2', name:'Vice President',       sort_order:2, description:'', is_active:true },
  { id:4, code:'L3', name:'General Manager',      sort_order:3, description:'', is_active:true },
  { id:5, code:'L4', name:'Manager',              sort_order:4, description:'', is_active:true },
  { id:6, code:'L5', name:'Executive / Officer',  sort_order:5, description:'', is_active:true },
  { id:7, code:'L6', name:'Trainee / Technician', sort_order:6, description:'', is_active:true },
];

// ─────────────────────────────────────────────────────────────
// SHARED UI ATOMS
// ─────────────────────────────────────────────────────────────

// Single RowActions used everywhere (was duplicated as RA vs RowActions)
function RowActions({ onEdit, onDelete }) {
  return (
    <div className={styles.row_actions}>
      <button className={styles.act_btn} title="Edit" onClick={e=>{e.stopPropagation();onEdit();}}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
        </svg>
      </button>
      <button className={`${styles.act_btn} ${styles.act_btn_del}`} title="Delete" onClick={e=>{e.stopPropagation();onDelete();}}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="3 6 5 6 21 6"/>
          <path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/>
        </svg>
      </button>
    </div>
  );
}

// Active / Inactive status pill
function ActiveBadge({ v }) {
  return (
    <span style={{
      display:'inline-block', padding:'2px 10px', borderRadius:20,
      fontSize:11, fontWeight:700,
      background: v ? '#dcfce7' : '#f1f5f9',
      color:       v ? '#15803d' : '#64748b',
    }}>{v ? 'Active' : 'Inactive'}</span>
  );
}

// Single MasterTable used everywhere (SetupPage had a slightly different version;
// this one is the superset — supports loading spinner + checkbox in modal via Modal below)
function MasterTable({ columns, data, searchKeys=[], defaultPerPage=10, onRefresh, loading=false }) {
  const [search,  setSearch]  = useState('');
  const [page,    setPage]    = useState(1);
  const [pp,      setPp]      = useState(defaultPerPage);
  const [sortKey, setSortKey] = useState('');
  const [sortDir, setSortDir] = useState('asc');

  useEffect(() => { setPage(1); }, [search, data.length]);

  const filtered = useMemo(() => {
    let d = [...data];
    if (search.trim()) {
      const q = search.toLowerCase();
      d = d.filter(row => searchKeys.some(k => String(row[k]||'').toLowerCase().includes(q)));
    }
    if (sortKey) d.sort((a,b) => {
      const av=String(a[sortKey]||''), bv=String(b[sortKey]||'');
      return sortDir==='asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });
    return d;
  }, [data, search, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pp));
  const safePage   = Math.min(page, totalPages);
  const slice      = filtered.slice((safePage-1)*pp, safePage*pp);
  const start      = Math.max(1, safePage-2);
  const end        = Math.min(totalPages, start+4);
  const pageNums   = Array.from({length:end-start+1},(_,i)=>start+i);

  const handleSort = key => {
    if (sortKey===key) setSortDir(d=>d==='asc'?'desc':'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  return (
    <div style={{display:'flex',flexDirection:'column',flex:1,overflow:'hidden',background:'#fff'}}>
      <div className={styles.table_toolbar}>
        <select className={styles.pp_select} value={pp} onChange={e=>{setPp(Number(e.target.value));setPage(1);}}>
          {[10,25,50,100].map(n=><option key={n} value={n}>{n}</option>)}
        </select>
        <button className={styles.export_btn} onClick={()=>toast('Export coming soon')}>Export</button>
        <button className={styles.refresh_btn} onClick={onRefresh} title="Refresh" disabled={loading}>
          <span style={{display:'inline-block',animation:loading?'spin 1s linear infinite':'none'}}>↻</span>
        </button>
        <div className={styles.tbl_search}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input placeholder="Search…" value={search} onChange={e=>setSearch(e.target.value)}/>
        </div>
      </div>

      <div className={styles.tbl_wrap}>
        <table className={styles.tbl}>
          <thead>
            <tr>
              {columns.map(col=>(
                <th key={col.key||col.label} onClick={col.key?()=>handleSort(col.key):undefined}
                    style={col.width?{width:col.width,minWidth:col.width}:{}}>
                  <div className={styles.th_inner}>
                    {col.label}
                    {col.key&&<span className={`${styles.sort_icon} ${sortKey===col.key?styles.sort_active:''}`}>
                      {sortKey===col.key?(sortDir==='asc'?'▲':'▼'):'⇅'}
                    </span>}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading
              ? <tr><td colSpan={columns.length} style={{textAlign:'center',padding:40,color:'#94a3b8'}}>Loading…</td></tr>
              : slice.length===0
              ? <tr><td colSpan={columns.length} className={styles.tbl_empty}>No records found</td></tr>
              : slice.map((row,i)=>(
                  <tr key={row.id||i}>
                    {columns.map(col=>(
                      <td key={col.key||col.label}>{col.render?col.render(row):(row[col.key]??'—')}</td>
                    ))}
                  </tr>
                ))
            }
          </tbody>
        </table>
      </div>

      <div className={styles.pagination}>
        <span>Showing {filtered.length===0?0:(safePage-1)*pp+1} to {Math.min(safePage*pp,filtered.length)} of {filtered.length} entries</span>
        <div style={{display:'flex',alignItems:'center',gap:10}}>
          <div className={styles.page_btns}>
            <button className={styles.pg_btn} onClick={()=>setPage(1)} disabled={safePage===1}>Previous</button>
            {pageNums.map(n=>(
              <button key={n} className={`${styles.pg_btn} ${safePage===n?styles.pg_btn_active:''}`} onClick={()=>setPage(n)}>{n}</button>
            ))}
            {end<totalPages&&<span style={{padding:'0 4px',color:'#94a3b8'}}>…</span>}
            <button className={styles.pg_btn} onClick={()=>setPage(p=>Math.min(p+1,totalPages))} disabled={safePage===totalPages}>Next</button>
          </div>
          <div className={styles.pg_goto}>
            <select value={safePage} onChange={e=>setPage(Number(e.target.value))}>
              {Array.from({length:totalPages},(_,i)=>i+1).map(n=><option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>
      </div>
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

// Single Modal — superset of FormModal + CMP Modal
// Supports: text, number, date, email, password, select, textarea, checkbox fields
// Grid layout auto-switches to 2-col when fields > 4
function Modal({ title, fields, initial={}, onClose, onSave, saving=false }) {
  const [form, setForm] = useState({...initial});
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const submit = () => {
    for (const f of fields.filter(x=>x.required)) {
      if (!form[f.key] && form[f.key] !== false) { toast.error(`${f.label} is required`); return; }
    }
    onSave(form);
  };
  const multiCol = fields.filter(f=>f.type!=='checkbox').length > 4;
  return (
    <div className={styles.overlay} >
      <div className={`${styles.modal} ${multiCol ? styles.modal_lg : styles.modal_sm}`} onClick={e=>e.stopPropagation()}>
        <div className={styles.modal_head}>
          <h2>{title}</h2>
          <button className={styles.modal_x} onClick={onClose}>✕</button>
        </div>
        <div className={styles.modal_body}>
          <div className={styles.form_grid} style={!multiCol?{gridTemplateColumns:'1fr'}:{}}>
            {fields.map(f=>(
              <div key={f.key} className={`${styles.fg} ${f.full?styles.form_full:''}`}>
                <label>{f.label}{f.required?' *':''}</label>
                {f.type==='select'
                  ? <select value={form[f.key]??''} onChange={e=>set(f.key,e.target.value)}>
                      <option value="">Select…</option>
                      {f.options.map(o=><option key={o.v??o} value={o.v??o}>{o.l??o}</option>)}
                    </select>
                  : f.type==='textarea'
                  ? <textarea rows={3} value={form[f.key]??''} onChange={e=>set(f.key,e.target.value)} placeholder={f.placeholder}/>
                  : f.type==='checkbox'
                  ? <label className={styles.toggle} style={{marginTop:8}}>
                      <input type="checkbox" checked={!!form[f.key]} onChange={e=>set(f.key,e.target.checked)}/>
                      <span className={styles.toggle_track}/>
                    </label>
                  : <input type={f.type||'text'} value={form[f.key]??''} onChange={e=>set(f.key,e.target.value)} placeholder={f.placeholder}/>
                }
              </div>
            ))}
          </div>
        </div>
        <div className={styles.modal_foot}>
          <button className={styles.btn_cancel} onClick={onClose}>Cancel</button>
          <button className={styles.btn_save} onClick={submit} disabled={saving}>{saving?'Saving…':'Save'}</button>
        </div>
      </div>
    </div>
  );
}

function ComingSoon({ name }) {
  return (
    <div className={styles.coming_soon}>
      <div className={styles.coming_soon_icon}>🚧</div>
      <div className={styles.coming_soon_title}>{name}</div>
      <div style={{fontSize:13}}>This module is coming soon</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// COMPANY MASTER — sub-pages (formerly CompanyMasterPage.jsx)
// ─────────────────────────────────────────────────────────────

function UnitBranchPage() {
  const [data,    setData]    = useState(DEMO_LOCATIONS);
  const [modal,   setModal]   = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await companyAPI.listLocations(); setData(r.data.data); }
    catch { /* use demo */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async row => {
    if (!window.confirm(`Delete "${row.location_name}"?`)) return;
    try { await companyAPI.deleteLocation(row.id); } catch {}
    setData(d=>d.filter(x=>x.id!==row.id));
    toast.success('Deleted');
  };

  const save = async form => {
    setSaving(true);
    try {
      if (modal==='add') {
        try { const r = await companyAPI.createLocation(form); setData(d=>[...d,r.data]); }
        catch { setData(d=>[...d,{id:Date.now(),...form}]); }
      } else {
        try { await companyAPI.updateLocation(modal.id,form); } catch {}
        setData(d=>d.map(x=>x.id===modal.id?{...x,...form}:x));
      }
      toast.success('Saved'); setModal(null);
    } finally { setSaving(false); }
  };

  const FIELDS = [
    { key:'location_code',            label:'Location Code',            required:true, placeholder:'e.g. CH012324' },
    { key:'location_name',            label:'Location Name',            required:true, placeholder:'e.g. Chakan 1, Mahalunge' },
    { key:'erp_location_code',        label:'ERP Location Code',        placeholder:'e.g. CH012324' },
    { key:'attendance_machine_code',  label:'Attendance Machine Code',  placeholder:'e.g. CH012324' },
    { key:'hrms_location_code',       label:'HRMS Location Code',       placeholder:'e.g. CH012324' },
    { key:'substitute_location_code', label:'Substitute Location Code', placeholder:'e.g. CH012324' },
    { key:'address', label:'Address', type:'textarea', placeholder:'Full address…', full:true },
    { key:'is_active', label:'Active', type:'checkbox' },
  ];

  const cols = [
    { key:'location_code',            label:'Location Code',            width:140 },
    { key:'erp_location_code',        label:'ERP Location Code',        width:155 },
    { key:'attendance_machine_code',  label:'Attendance Machine Code',  width:190 },
    { key:'hrms_location_code',       label:'HRMS Location Code',       width:165 },
    { key:'substitute_location_code', label:'Substitute Location Code', width:190 },
    { key:'location_name',            label:'Location Name',            width:180 },
    { key:'address',                  label:'Address' },
    { label:'Options', width:80, render:r=><RowActions onEdit={()=>setModal(r)} onDelete={()=>del(r)}/> },
  ];

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%'}}>
      <div className={styles.page_bar}>
        <button className={styles.back_btn}>←</button>
        <h1 className={styles.page_title}>Company Master</h1>
        <button className={styles.add_btn} onClick={()=>setModal('add')}>＋ Add Location</button>
        <button className={styles.import_btn} onClick={()=>toast('Import coming soon')}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M16 16l-4-4-4 4M12 12V4"/><path d="M20 16v4a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-4"/>
          </svg>
          Import Locations
        </button>
      </div>
      <MasterTable columns={cols} data={data} searchKeys={['location_code','location_name','address']} defaultPerPage={10} onRefresh={load} loading={loading}/>
      {modal&&<Modal title={modal==='add'?'Add Location':'Edit Location'} fields={FIELDS} initial={modal==='add'?{is_active:true}:modal} onClose={()=>setModal(null)} onSave={save} saving={saving}/>}
    </div>
  );
}

// Generic factory for simple CRUD master pages
function SimpleMasterPage({ addLabel, demo, apiList, apiCreate, apiUpdate, apiDelete, columns, fields, searchKeys }) {
  const [data,    setData]    = useState(demo);
  const [modal,   setModal]   = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await apiList(); setData(r.data.data); }
    catch {}
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async row => {
    if (!window.confirm('Delete this record?')) return;
    try { await apiDelete(row.id); } catch {}
    setData(d=>d.filter(x=>x.id!==row.id));
    toast.success('Deleted');
  };

  const save = async form => {
    setSaving(true);
    try {
      if (modal==='add') {
        try { const r = await apiCreate(form); setData(d=>[...d,r.data]); }
        catch { setData(d=>[...d,{id:Date.now(),...form}]); }
      } else {
        try { await apiUpdate(modal.id,form); } catch {}
        setData(d=>d.map(x=>x.id===modal.id?{...x,...form}:x));
      }
      toast.success('Saved'); setModal(null);
    } finally { setSaving(false); }
  };

  const fullCols = [...columns, { label:'Options', width:80, render:r=><RowActions onEdit={()=>setModal(r)} onDelete={()=>del(r)}/> }];

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%'}}>
      <div className={styles.page_bar}>
        <button className={styles.back_btn}>←</button>
        <h1 className={styles.page_title}>Company Master</h1>
        <button className={styles.add_btn} onClick={()=>setModal('add')}>＋ {addLabel}</button>
      </div>
      <MasterTable columns={fullCols} data={data} searchKeys={searchKeys} defaultPerPage={10} onRefresh={load} loading={loading}/>
      {modal&&<Modal title={`${modal==='add'?'Add':'Edit'} ${addLabel}`} fields={fields} initial={modal==='add'?{is_active:true}:modal} onClose={()=>setModal(null)} onSave={save} saving={saving}/>}
    </div>
  );
}

function DepartmentPage() {
  return <SimpleMasterPage addLabel="Department" demo={DEMO_DEPTS}
    apiList={companyAPI.listDepartments} apiCreate={companyAPI.createDepartment}
    apiUpdate={companyAPI.updateDepartment} apiDelete={companyAPI.deleteDepartment}
    columns={[{key:'name',label:'Department Name'},{key:'code',label:'Code',width:100},{label:'Status',width:100,render:r=><ActiveBadge v={r.is_active}/>}]}
    fields={[{key:'name',label:'Department Name',required:true},{key:'code',label:'Code',placeholder:'e.g. ACC'},{key:'description',label:'Description',type:'textarea'},{key:'is_active',label:'Active',type:'checkbox'}]}
    searchKeys={['name','code']}/>;
}

function FunctionalAreaPage() {
  return <SimpleMasterPage addLabel="Functional Area" demo={DEMO_FA}
    apiList={companyAPI.listFunctionalAreas} apiCreate={companyAPI.createFunctionalArea}
    apiUpdate={companyAPI.updateFunctionalArea} apiDelete={companyAPI.deleteFunctionalArea}
    columns={[{key:'name',label:'Functional Area Name'},{key:'code',label:'Code',width:100},{label:'Status',width:100,render:r=><ActiveBadge v={r.is_active}/>}]}
    fields={[{key:'name',label:'Functional Area Name',required:true},{key:'code',label:'Code',placeholder:'e.g. MFG'},{key:'description',label:'Description',type:'textarea'},{key:'is_active',label:'Active',type:'checkbox'}]}
    searchKeys={['name','code']}/>;
}

// ── Currency Modal ────────────────────────────────────────────
const SEPARATOR_OPTIONS = [
  { v:',',           l:', (comma)'      },
  { v:'.',           l:'. (period)'     },
  { v:"'",           l:"' (apostrophe)" },
  { v:'none',        l:'none'           },
  { v:' ',           l:'space'          },
];

function CurrencyModal({ initial, onClose, onSave, saving }) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState({
    code:               initial?.code               || '',
    name:               initial?.name               || '',
    symbol:             initial?.symbol             || '',
    decimal_separator:  initial?.decimal_separator  || '.',
    thousand_separator: initial?.thousand_separator || ',',
    currency_placement: initial?.currency_placement || 'before',
    is_default:         initial?.is_default         || false,
    is_active:          initial?.is_active          ?? true,
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = () => {
    if (!form.code.trim())   { toast.error('Currency Code is required'); return; }
    if (!form.symbol.trim()) { toast.error('Symbol is required'); return; }
    onSave(form);
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} style={{ width: 560 }} onClick={e => e.stopPropagation()}>
        <div className={styles.modal_head}>
          <h2>{isEdit ? 'Edit Currency' : 'Add Currency'}</h2>
          <button className={styles.modal_x} onClick={onClose}>✕</button>
        </div>

        <div className={styles.modal_body}>
          {/* Info banner */}
          <div style={{ background:'#fefce8', border:'1px solid #fde68a', borderRadius:8, padding:'10px 14px', fontSize:13, color:'#92400e', marginBottom:20 }}>
            Make sure to enter valid currency ISO code.
          </div>

          {/* Currency Code */}
          <div className={styles.fg} style={{ marginBottom:14 }}>
            <label>* Currency Code</label>
            <input value={form.code} onChange={e => set('code', e.target.value.toUpperCase())} placeholder="e.g. EUR"/>
          </div>

          {/* Symbol */}
          <div className={styles.fg} style={{ marginBottom:14 }}>
            <label>* Symbol</label>
            <input value={form.symbol} onChange={e => set('symbol', e.target.value)} placeholder="e.g. €"/>
          </div>

          {/* Decimal + Thousand Separator side by side */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:14 }}>
            <div className={styles.fg}>
              <label>* Decimal Separator</label>
              <select value={form.decimal_separator} onChange={e => set('decimal_separator', e.target.value)}>
                {SEPARATOR_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
            </div>
            <div className={styles.fg}>
              <label>* Thousand Separator</label>
              <select value={form.thousand_separator} onChange={e => set('thousand_separator', e.target.value)}>
                {SEPARATOR_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
              </select>
            </div>
          </div>

          {/* Currency Placement */}
          <div className={styles.fg} style={{ marginBottom:14 }}>
            <label>* Currency Placement</label>
            <div style={{ display:'flex', alignItems:'center', gap:24, marginTop:8 }}>
              {[['before','Before Amount'],['after','After Amount']].map(([v,l]) => (
                <label key={v} style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:13, color:'#374151', fontWeight:400 }}>
                  <input
                    type="radio"
                    name="currency_placement"
                    value={v}
                    checked={form.currency_placement === v}
                    onChange={() => set('currency_placement', v)}
                    style={{ accentColor:'#1a56db', width:16, height:16, cursor:'pointer' }}
                  />
                  {l}
                </label>
              ))}
            </div>
          </div>

          {/* Set as Default toggle */}
          <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:6 }}>
            <label className={styles.toggle}>
              <input type="checkbox" checked={form.is_default} onChange={e => set('is_default', e.target.checked)}/>
              <span className={styles.toggle_track}/>
            </label>
            <span style={{ fontSize:13, color:'#374151' }}>Set as Default Currency</span>
          </div>

          {/* Active toggle */}
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <label className={styles.toggle}>
              <input type="checkbox" checked={form.is_active} onChange={e => set('is_active', e.target.checked)}/>
              <span className={styles.toggle_track}/>
            </label>
            <span style={{ fontSize:13, color:'#374151' }}>Active</span>
          </div>
        </div>

        <div className={styles.modal_foot}>
          <button className={styles.btn_cancel} onClick={onClose}>Cancel</button>
          <button className={styles.btn_save} onClick={submit} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

function CurrencyPage() {
  const [data,    setData]    = useState(DEMO_CURR);
  const [modal,   setModal]   = useState(null); // null | 'add' | row-object
  const [saving,  setSaving]  = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await companyAPI.listCurrencies(); setData(r.data.data); }
    catch {}
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async row => {
    if (!window.confirm(`Delete "${row.name}"?`)) return;
    try { await companyAPI.deleteCurrency(row.id); } catch {}
    setData(d => d.filter(x => x.id !== row.id));
    toast.success('Deleted');
  };

  const save = async form => {
    setSaving(true);
    try {
      if (modal === 'add') {
        try { const r = await companyAPI.createCurrency(form); setData(d => [...d, r.data]); }
        catch { setData(d => [...d, { id: Date.now(), ...form }]); }
      } else {
        try { await companyAPI.updateCurrency(modal.id, form); } catch {}
        setData(d => d.map(x => x.id === modal.id ? { ...x, ...form } : x));
      }
      toast.success('Saved'); setModal(null);
    } finally { setSaving(false); }
  };

  // Preview: show formatted amount using current separators
  const fmtPreview = row => {
    const dec = row.decimal_separator || '.';
    const tho = row.thousand_separator === 'none' ? '' : (row.thousand_separator || ',');
    const num = `1${tho}234${dec}56`;
    return row.currency_placement === 'after' ? `${num} ${row.symbol}` : `${row.symbol}${num}`;
  };

  const cols = [
    { key:'code',   label:'Code',          width:80  },
    { key:'symbol', label:'Symbol',        width:70  },
    { key:'name',   label:'Currency Name'            },
    { label:'Preview', width:130, render:r=><span style={{fontSize:12,color:'#4a5568',fontFamily:'monospace'}}>{fmtPreview(r)}</span> },
    { label:'Default', width:90,  render:r=><ActiveBadge v={r.is_default}/> },
    { label:'Status',  width:90,  render:r=><ActiveBadge v={r.is_active}/> },
    { label:'Options', width:80,  render:r=><RowActions onEdit={()=>setModal(r)} onDelete={()=>del(r)}/> },
  ];

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%'}}>
      <div className={styles.page_bar}>
        <button className={styles.back_btn}>←</button>
        <h1 className={styles.page_title}>Company Master</h1>
        <button className={styles.add_btn} onClick={()=>setModal('add')}>＋ Add Currency</button>
      </div>
      <MasterTable columns={cols} data={data} searchKeys={['code','name']} defaultPerPage={10} onRefresh={load} loading={loading}/>
      {modal && (
        <CurrencyModal
          initial={modal === 'add' ? null : modal}
          onClose={() => setModal(null)}
          onSave={save}
          saving={saving}
        />
      )}
    </div>
  );
}

function EventLocationPage() {
  return <SimpleMasterPage addLabel="Event Location" demo={DEMO_EVTLOC}
    apiList={companyAPI.listEventLocations} apiCreate={companyAPI.createEventLocation}
    apiUpdate={companyAPI.updateEventLocation} apiDelete={companyAPI.deleteEventLocation}
    columns={[{key:'name',label:'Location Name'},{key:'code',label:'Code',width:100},{key:'capacity',label:'Capacity',width:100},{key:'address',label:'Address'},{label:'Status',width:90,render:r=><ActiveBadge v={r.is_active}/>}]}
    fields={[{key:'name',label:'Location Name',required:true},{key:'code',label:'Code'},{key:'capacity',label:'Capacity',type:'number'},{key:'address',label:'Address',type:'textarea'},{key:'is_active',label:'Active',type:'checkbox'}]}
    searchKeys={['name','code','address']}/>;
}

function FinancialYearPage() {
  return <SimpleMasterPage addLabel="Financial Year" demo={DEMO_FY}
    apiList={companyAPI.listFinancialYears} apiCreate={companyAPI.createFinancialYear}
    apiUpdate={companyAPI.updateFinancialYear} apiDelete={companyAPI.deleteFinancialYear}
    columns={[{key:'label',label:'Financial Year'},{key:'start_date',label:'Start Date',width:120},{key:'end_date',label:'End Date',width:120},{label:'Current',width:90,render:r=><ActiveBadge v={r.is_current}/>},{label:'Status',width:90,render:r=><ActiveBadge v={r.is_active}/>}]}
    fields={[{key:'label',label:'Label',required:true,placeholder:'e.g. FY 2025-26'},{key:'start_date',label:'Start Date',required:true,type:'date'},{key:'end_date',label:'End Date',required:true,type:'date'},{key:'is_current',label:'Set as Current',type:'checkbox'},{key:'is_active',label:'Active',type:'checkbox'}]}
    searchKeys={['label']}/>;
}

function OrgLevelsPage() {
  return <SimpleMasterPage addLabel="Org Level" demo={DEMO_ORGLEVELS}
    apiList={companyAPI.listOrgLevels} apiCreate={companyAPI.createOrgLevel}
    apiUpdate={companyAPI.updateOrgLevel} apiDelete={companyAPI.deleteOrgLevel}
    columns={[{key:'code',label:'Level Code',width:110},{key:'name',label:'Level Name'},{key:'sort_order',label:'Sort Order',width:110},{label:'Status',width:90,render:r=><ActiveBadge v={r.is_active}/>}]}
    fields={[{key:'code',label:'Level Code',required:true,placeholder:'e.g. L4'},{key:'name',label:'Level Name',required:true,placeholder:'e.g. Manager'},{key:'sort_order',label:'Sort Order',type:'number',placeholder:'0'},{key:'description',label:'Description',type:'textarea'},{key:'is_active',label:'Active',type:'checkbox'}]}
    searchKeys={['code','name']}/>;
}

const DEFAULT_SETTINGS = [
  { key:'company_name',         label:'Company Name',          value:'',          group:'General'  },
  { key:'company_logo',         label:'Company Logo URL',      value:'',          group:'General'  },
  { key:'support_email',        label:'Support Email',         value:'',          group:'Contact'  },
  { key:'support_phone',        label:'Support Phone',         value:'',          group:'Contact'  },
  { key:'date_format',          label:'Date Format',           value:'DD-MM-YYYY',group:'Regional' },
  { key:'default_timezone',     label:'Default Timezone',      value:'Asia/Kolkata',group:'Regional'},
  { key:'default_currency',     label:'Default Currency',      value:'INR',       group:'Regional' },
  { key:'max_file_upload_mb',   label:'Max File Upload (MB)',  value:'5',         group:'System'   },
  { key:'session_timeout_min',  label:'Session Timeout (min)', value:'60',        group:'System'   },
];

function GeneralSettingPage() {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [saving,   setSaving]   = useState(false);
  const [dirty,    setDirty]    = useState(false);

  // Load from API on mount
  useEffect(() => {
    companyAPI.getSettings().then(r => {
      const fromAPI = r.data?.data || [];
      setSettings(DEFAULT_SETTINGS.map(def => {
        const found = fromAPI.find(a => a.key === def.key);
        return found ? { ...def, value: found.value ?? def.value } : def;
      }));
    }).catch(() => {}); // keep defaults on error
  }, []);

  const groups = [...new Set(settings.map(s => s.group))];
  const set = (key, v) => { setSettings(s => s.map(x => x.key === key ? {...x, value: v} : x)); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      await companyAPI.saveSettings(settings.map(s => ({ key:s.key, value:s.value, label:s.label, group:s.group })));
      toast.success('Settings saved');
      setDirty(false);
    } catch {
      toast.error('Failed to save settings');
    } finally { setSaving(false); }
  };

  const inputStyle = {
    width:'100%', padding:'8px 12px', border:'1px solid #d1d5db',
    borderRadius:7, fontSize:13, fontFamily:'inherit',
    outline:'none', background:'#fff', color:'#1e2a3b',
    boxSizing:'border-box', transition:'border-color .15s',
  };

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%'}}>
      <div className={styles.page_bar}>
        <button className={styles.back_btn}>←</button>
        <h1 className={styles.page_title}>Company Master — General Settings</h1>
        <button
          className={styles.add_btn}
          onClick={save}
          disabled={saving || !dirty}
          style={{ opacity: dirty ? 1 : 0.5, cursor: dirty ? 'pointer' : 'not-allowed' }}
        >
          {saving ? 'Saving…' : '💾 Save All'}
        </button>
      </div>
      <div style={{flex:1,overflowY:'auto',padding:'20px 28px',background:'#f5f6fa'}}>
        {dirty && (
          <div style={{background:'#fff7ed',border:'1px solid #fed7aa',borderRadius:8,padding:'8px 16px',marginBottom:14,fontSize:12,color:'#c2410c',fontWeight:500}}>
            ● You have unsaved changes — click "Save All" to apply
          </div>
        )}
        {groups.map(grp => (
          <div key={grp} style={{background:'#fff',border:'1px solid #e2e8f0',borderRadius:12,marginBottom:18,overflow:'hidden'}}>
            <div style={{padding:'12px 20px',background:'#f8f9fc',borderBottom:'1px solid #e2e8f0',fontSize:12,fontWeight:700,textTransform:'uppercase',letterSpacing:'.06em',color:'#64748b'}}>{grp}</div>
            <div style={{padding:'16px 20px',display:'grid',gridTemplateColumns:'1fr 1fr',gap:'14px 24px'}}>
              {settings.filter(s => s.group === grp).map(s => (
                <div key={s.key}>
                  <label style={{display:'block',fontSize:12,fontWeight:600,color:'#374151',marginBottom:5}}>{s.label}</label>
                  <input
                    value={s.value}
                    onChange={e => set(s.key, e.target.value)}
                    placeholder={`Enter ${s.label}`}
                    style={inputStyle}
                    onFocus={e => e.target.style.borderColor='#1a56db'}
                    onBlur={e => e.target.style.borderColor='#d1d5db'}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ThemeStylePage() {
  const { theme: appliedTheme, saveTheme, resetTheme } = useTheme();
  const [local, setLocal]   = useState({});
  const [saving, setSaving] = useState(false);
  const [dirty,  setDirty]  = useState(false);

  // Sync local from applied theme on mount / external change
  useEffect(() => { setLocal({...THEME_DEFAULTS, ...appliedTheme}); setDirty(false); }, [appliedTheme]);

  const set = (k, v) => { setLocal(p => ({...p, [k]: v})); setDirty(true); };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveTheme(local);
      toast.success('Theme saved and applied');
      setDirty(false);
    } catch { toast.error('Failed to save theme'); }
    finally { setSaving(false); }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset theme to defaults?')) return;
    setSaving(true);
    try {
      await resetTheme();
      toast.success('Theme reset to defaults');
      setDirty(false);
    } catch { toast.error('Failed to reset'); }
    finally { setSaving(false); }
  };

  const FIELD_ORDER = [
    'theme_header_bg',
    'theme_header_text',
    'theme_header_svg',
    'theme_sidebar_bg',
    'theme_sidebar_links',
    'theme_sidebar_svg',
    'theme_sidebar_active_item',
    'theme_sidebar_active_item_bg',
    'theme_sidebar_svg_hover',
    'theme_sidebar_svg_active_mini',
    'theme_topnav_hover_bg',
    'theme_sidebar_active_bg',
  ];

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%'}}>
      <div className={styles.page_bar}>
        <button className={styles.back_btn}>←</button>
        <h1 className={styles.page_title}>Company Master</h1>
      </div>
      <div style={{flex:1,overflowY:'auto',padding:'24px 32px',background:'#f5f6fa'}}>
        <div style={{background:'#fff',border:'1px solid #e2e8f0',borderRadius:12,padding:'28px',maxWidth:860}}>

          {FIELD_ORDER.map(key => {
            const label   = THEME_LABELS[key] || key;
            const val     = local[key] || THEME_DEFAULTS[key];
            const isColor = val.startsWith('#') || val.startsWith('rgb');
            return (
              <div key={key} style={{marginBottom:18}}>
                <label style={{display:'block',fontSize:12,fontWeight:500,color:'#374151',marginBottom:6}}>
                  {label}
                </label>
                <div style={{display:'flex',alignItems:'center',border:'1px solid #d1d5db',borderRadius:7,overflow:'hidden',background:'#fff'}}>
                  <input
                    type="text"
                    value={val}
                    onChange={e => set(key, e.target.value)}
                    style={{
                      flex:1, border:'none', outline:'none',
                      padding:'9px 12px', fontSize:13, color:'#374151',
                      fontFamily:'monospace', background:'transparent',
                    }}
                  />
                  {/* Color swatch / picker */}
                  <label style={{
                    display:'flex', alignItems:'center', justifyContent:'center',
                    width:38, height:38, cursor:'pointer', flexShrink:0,
                    borderLeft:'1px solid #e5e7eb',
                    background: isColor ? val : '#e5e7eb',
                    position:'relative',
                  }}>
                    <input
                      type="color"
                      value={isColor && val.length===7 ? val : '#1a56db'}
                      onChange={e => set(key, e.target.value)}
                      style={{opacity:0, position:'absolute', width:'100%', height:'100%', cursor:'pointer', border:'none', padding:0}}
                    />
                  </label>
                </div>
              </div>
            );
          })}

          {/* Live preview strip */}
          <div style={{marginTop:28,borderRadius:8,overflow:'hidden',border:'1px solid #e2e8f0'}}>
            <div style={{
              background: local.theme_header_bg || THEME_DEFAULTS.theme_header_bg,
              color:       local.theme_header_text || THEME_DEFAULTS.theme_header_text,
              padding:'10px 18px', fontSize:13, fontWeight:600,
              display:'flex', alignItems:'center', gap:10,
            }}>
              <span style={{opacity:.8}}>▶</span> Header Preview
            </div>
            <div style={{display:'flex',height:48}}>
              <div style={{
                width:120, background: local.theme_sidebar_bg || THEME_DEFAULTS.theme_sidebar_bg,
                borderRight:'1px solid #e2e8f0',
                display:'flex',alignItems:'center',justifyContent:'center',
                fontSize:11, color: local.theme_sidebar_links || THEME_DEFAULTS.theme_sidebar_links,
              }}>Sidebar</div>
              <div style={{flex:1,display:'flex',alignItems:'center',padding:'0 16px',
                background: local.theme_sidebar_active_bg || THEME_DEFAULTS.theme_sidebar_active_bg,
                color: local.theme_sidebar_active_item || THEME_DEFAULTS.theme_sidebar_active_item,
                fontSize:12,fontWeight:600}}>
                Active Item
              </div>
            </div>
          </div>

          {/* Save / Reset buttons */}
          <div style={{display:'flex',justifyContent:'flex-end',gap:10,marginTop:24}}>
            <button
              onClick={handleReset} disabled={saving}
              style={{padding:'8px 20px',borderRadius:7,border:'1px solid #d1d5db',
                background:'#fff',color:'#374151',fontSize:13,cursor:'pointer',fontFamily:'inherit'}}>
              Reset
            </button>
            <button
              onClick={handleSave} disabled={saving || !dirty}
              style={{padding:'8px 22px',borderRadius:7,border:'none',
                background: dirty ? '#1a56db' : '#93c5fd',
                color:'#fff',fontSize:13,fontWeight:600,cursor: dirty ? 'pointer' : 'not-allowed',fontFamily:'inherit'}}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}

function DynamicMastersPage() {
  const [masters, setMasters] = useState([
    { id:1, name:'Blood Group',    values:'A+, A-, B+, B-, O+, O-, AB+, AB-' },
    { id:2, name:'Marital Status', values:'Single, Married, Divorced, Widowed' },
    { id:3, name:'Shift',          values:'General, Morning, Afternoon, Night' },
    { id:4, name:'Grade',          values:'E1, E2, E3, M1, M2, M3, S1, S2'   },
  ]);
  const [modal, setModal] = useState(null);
  const cols = [
    { key:'name',   label:'Master Name' },
    { key:'values', label:'Values', render:r=><span style={{fontSize:12,color:'#4a5568'}}>{r.values}</span> },
    { label:'Options', width:80, render:r=><RowActions onEdit={()=>setModal(r)} onDelete={()=>setMasters(d=>d.filter(x=>x.id!==r.id))}/> },
  ];
  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%'}}>
      <div className={styles.page_bar}>
        <button className={styles.back_btn}>←</button>
        <h1 className={styles.page_title}>Company Master — Dynamic Masters</h1>
        <button className={styles.add_btn} onClick={()=>setModal('add')}>＋ Add Master</button>
      </div>
      <MasterTable columns={cols} data={masters} searchKeys={['name']} defaultPerPage={25} onRefresh={()=>{}} loading={false}/>
      {modal&&<Modal
        title={modal==='add'?'Add Dynamic Master':'Edit Dynamic Master'}
        fields={[{key:'name',label:'Master Name',required:true},{key:'values',label:'Values (comma-separated)',type:'textarea',placeholder:'e.g. A+, A-, B+'}]}
        initial={modal==='add'?{}:modal}
        onClose={()=>setModal(null)}
        onSave={form=>{
          if(modal==='add') setMasters(d=>[...d,{id:Date.now(),...form}]);
          else setMasters(d=>d.map(x=>x.id===modal.id?{...x,...form}:x));
          toast.success('Saved'); setModal(null);
        }}
      />}
    </div>
  );
}



// ─────────────────────────────────────────────────────────────
// USER MASTER PAGES
// ─────────────────────────────────────────────────────────────

/* ─── shared mini helpers used by User Master pages ─────────── */
function UMGrid({ cols, rows, loading, onAdd, addLabel, onRefresh, children }) {
  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%'}}>
      <div className={styles.page_bar}>
        <button className={styles.back_btn}>←</button>
        <h1 className={styles.page_title}>User Master</h1>
        <button className={styles.add_btn} onClick={onAdd}>＋ {addLabel}</button>
        {onRefresh && <button className={styles.import_btn} onClick={onRefresh}>↻ Refresh</button>}
      </div>
      {loading
        ? <div style={{textAlign:'center',padding:40,color:'#94a3b8',fontSize:13}}>Loading…</div>
        : <MasterTable columns={cols} data={rows} searchKeys={cols.filter(c=>c.key).map(c=>c.key)} defaultPerPage={25}/>}
      {children}
    </div>
  );
}

/* ── DesignationPage ─────────────────────────────────────────── */
function DesignationPage() {
  const [data,    setData]    = useState([]);
  const [modal,   setModal]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);

  const load = () => {
    setLoading(true);
    userMastersAPI.listDesignations()
      .then(r => setData(r.data||[]))
      .catch(()=>{})
      .finally(()=>setLoading(false));
  };
  useEffect(load, []);

  const save = async (form) => {
    if (!form.name?.trim()) { toast.error('Designation name is required'); return; }
    setSaving(true);
    try {
      const p = {name:form.name.trim(), org_level:form.org_level||null, is_active:form.is_active!==false};
      if (modal?.id) await userMastersAPI.updateDesignation(modal.id, p);
      else           await userMastersAPI.createDesignation(p);
      toast.success(modal?.id ? 'Designation updated' : 'Designation created');
      setModal(null); load();
    } catch(e) { toast.error(e?.response?.data?.detail||'Save failed'); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Deactivate this designation?')) return;
    try { await userMastersAPI.deleteDesignation(id); toast.success('Deactivated'); load(); }
    catch { toast.error('Failed'); }
  };

  const IS = {width:'100%',border:'1.5px solid #e2e8f0',borderRadius:7,padding:'8px 11px',fontSize:13,outline:'none',boxSizing:'border-box',background:'#fff'};
  const LS = {fontSize:12,fontWeight:600,color:'#475569',display:'block',marginBottom:4};

  return (
    <UMGrid
      cols={[
        {key:'name',      label:'Designation Name'},
        {key:'org_level', label:'Organisation Level'},
        {key:'is_active', label:'Active', render:r=>r.is_active?'✅ Yes':'❌ No'},
        {label:'Options', width:100, render:r=><RowActions onEdit={()=>setModal(r)} onDelete={()=>del(r.id)}/>},
      ]}
      rows={data} loading={loading} addLabel="Add Designation" onAdd={()=>setModal({is_active:true})} onRefresh={load}>
      {modal && (
        <Modal title={modal?.id?'Edit Designation':'Add Designation'}
          fields={[
            {key:'name',      label:'Designation Name *', required:true, placeholder:'e.g. Senior Engineer'},
            {key:'org_level', label:'Organisation Level', placeholder:'e.g. L4'},
            {key:'is_active', label:'Active', type:'checkbox'},
          ]}
          initial={modal} onClose={()=>setModal(null)} onSave={save}/>
      )}
    </UMGrid>
  );
}

/* ── RolePage ────────────────────────────────────────────────── */
function RolePage() {
  const [data,    setData]    = useState([]);
  const [modal,   setModal]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);

  const load = () => {
    setLoading(true);
    userMastersAPI.listRoles()
      .then(r => setData(r.data||[]))
      .catch(()=>{})
      .finally(()=>setLoading(false));
  };
  useEffect(load, []);

  const save = async (form) => {
    if (!form.name?.trim()) { toast.error('Role name is required'); return; }
    setSaving(true);
    try {
      const p = {name:form.name.trim()};
      if (modal?.id) await userMastersAPI.updateRole(modal.id, p);
      else           await userMastersAPI.createRole(p);
      toast.success(modal?.id ? 'Role updated' : 'Role created');
      setModal(null); load();
    } catch(e) { toast.error(e?.response?.data?.detail||'Save failed'); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this role? This will fail if users are assigned to it.')) return;
    try { await userMastersAPI.deleteRole(id); toast.success('Deleted'); load(); }
    catch(e) { toast.error(e?.response?.data?.detail||'Delete failed'); }
  };

  return (
    <UMGrid
      cols={[
        {label:'Role Name', render:r=>(
          <div>
            <div className={styles.user_link}>{r.name}</div>
            <div className={styles.role_sub}>Total Users: {r.total_users||0}</div>
          </div>
        )},
        {key:'slug', label:'Slug'},
        {label:'Options', width:100, render:r=><RowActions onEdit={()=>setModal(r)} onDelete={()=>del(r.id)}/>},
      ]}
      rows={data} loading={loading} addLabel="New Role" onAdd={()=>setModal({})} onRefresh={load}>
      {modal !== null && (
        <Modal title={modal?.id?'Edit Role':'New Role'}
          fields={[{key:'name',label:'Role Name *',required:true,placeholder:'e.g. Technician'}]}
          initial={modal} onClose={()=>setModal(null)} onSave={save}/>
      )}
    </UMGrid>
  );
}

/* ── UserListPage ────────────────────────────────────────────── */
function UserListPage() {
  const [data,      setData]      = useState([]);
  const [modal,     setModal]     = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState(false);
  const [hdrSearch, setHdrSearch] = useState('');
  const [roles,     setRoles]     = useState([]);
  const [desigs,    setDesigs]    = useState([]);

  const load = () => {
    setLoading(true);
    usersApiHook.list()
      .then(r => setData(r.data?.data||r.data||[]))
      .catch(()=>{})
      .finally(()=>setLoading(false));
  };

  useEffect(()=>{
    load();
    userMastersAPI.listRoles().then(r=>setRoles(r.data||[])).catch(()=>{});
    userMastersAPI.listDesignations().then(r=>setDesigs(r.data||[])).catch(()=>{});
  },[]);

  const save = async (form) => {
    if (!form.name?.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      const p = {
        name:            form.name,
        email:           form.email,
        department:      form.department||null,
        phone:           form.phone||null,
        employee_id:     form.employee_id||null,
        role_id:         form.role_id ? parseInt(form.role_id) : undefined,
        designation:     form.designation||null,
        functional_area: form.functional_area||null,
        is_active:       form.is_active !== false,
        ...(form.password ? {password: form.password} : {}),
      };
      if (modal?.id) {
        await usersApiHook.update(modal.id, p);
        toast.success('User updated');
      } else {
        if (!form.email || !form.password) { toast.error('Email and Password are required'); setSaving(false); return; }
        await usersApiHook.create(p);
        toast.success('User created');
      }
      setModal(null); load();
    } catch(e) { toast.error(e?.response?.data?.detail||'Save failed'); }
    finally { setSaving(false); }
  };

  const toggle = async (id) => {
    try { await usersApiHook.toggle(id); load(); }
    catch { toast.error('Failed'); }
  };

  const del = async (id) => {
    if (!window.confirm('Deactivate this user?')) return;
    try { await usersApiHook.delete(id); toast.success('Deactivated'); load(); }
    catch(e) { toast.error(e?.response?.data?.detail||'Failed'); }
  };

  const initials = name => name?.split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase()||'?';
  const displayed = hdrSearch
    ? data.filter(u=>u.name?.toLowerCase().includes(hdrSearch.toLowerCase()))
    : data;

  const roleOpts  = roles.map(r=>({value:r.id,  label:r.name}));
  const desigOpts = desigs.map(d=>({value:d.name,label:d.name}));

  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%'}}>
      <div className={styles.page_bar}>
        <button className={styles.back_btn}>←</button>
        <h1 className={styles.page_title}>User Master</h1>
        <button className={styles.add_btn} onClick={()=>setModal({is_active:true})}>＋ New User</button>
        <div style={{flex:1,maxWidth:400,display:'flex',alignItems:'center',gap:6,border:'1px solid #dde1ea',borderRadius:7,height:34,padding:'0 10px',background:'#f8f9fc'}}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input style={{border:'none',background:'transparent',outline:'none',fontSize:13,fontFamily:'inherit',flex:1,color:'#1e2a3b'}} placeholder="Search users…" value={hdrSearch} onChange={e=>setHdrSearch(e.target.value)}/>
          {hdrSearch&&<button onClick={()=>setHdrSearch('')} style={{background:'none',border:'none',cursor:'pointer',color:'#94a3b8'}}>✕</button>}
        </div>
        <button className={styles.green_btn} onClick={load}>↻ Refresh</button>
      </div>

      {loading
        ? <div style={{textAlign:'center',padding:40,color:'#94a3b8',fontSize:13}}>Loading…</div>
        : <MasterTable
            columns={[
              {key:'name', label:'Full Name', render:r=>(
                <div className={styles.avatar_cell}>
                  <div className={styles.avatar_sm}>{initials(r.name)}</div>
                  <span className={styles.user_link}>{r.name}</span>
                </div>
              )},
              {key:'designation',  label:'Designation'},
              {key:'department',   label:'Department'},
              {key:'role_name',    label:'Role'},
              {key:'email',        label:'Email'},
              {key:'phone',        label:'Mobile'},
              {label:'Active', width:72, render:r=>(
                <label className={styles.toggle}>
                  <input type="checkbox" checked={!!r.is_active} onChange={()=>toggle(r.id)}/>
                  <span className={styles.toggle_track}/>
                </label>
              )},
              {label:'Options', width:80, render:r=><RowActions onEdit={()=>setModal(r)} onDelete={()=>del(r.id)}/>},
            ]}
            data={displayed} searchKeys={['name','email','department','designation']} defaultPerPage={15}/>}

      {modal !== null && (
        <UserModal
          modal={modal} setModal={setModal}
          roleOpts={roleOpts} desigOpts={desigOpts}
          save={save} saving={saving} isNew={!modal?.id}/>
      )}
    </div>
  );
}

/* Custom user modal with dropdowns */
function UserModal({ modal, setModal, roleOpts, desigOpts, save, saving, isNew }) {
  const [form, setForm] = useState({is_active:true,...modal});
  const s = (k,v) => setForm(f=>({...f,[k]:v}));
  const IS = {width:'100%',border:'1.5px solid #e2e8f0',borderRadius:7,padding:'8px 11px',fontSize:13,outline:'none',boxSizing:'border-box',background:'#fff'};
  const LS = {fontSize:12,fontWeight:600,color:'#475569',display:'block',marginBottom:4};
  const G2 = {display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px 16px'};
  return (
    <div style={{position:'fixed',inset:0,background:'rgba(10,14,26,.55)',backdropFilter:'blur(4px)',zIndex:1200,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
      <div style={{background:'#fff',borderRadius:12,width:580,maxWidth:'96vw',maxHeight:'92vh',display:'flex',flexDirection:'column',boxShadow:'0 24px 64px rgba(0,0,0,.2)',overflow:'hidden'}}>
        <div style={{padding:'16px 20px 12px',borderBottom:'1px solid #e2e8f0',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <div style={{fontSize:16,fontWeight:700,color:'#1a1f2e'}}>{isNew?'+ New User':'✏ Edit User'}</div>
          <button onClick={()=>setModal(null)} style={{background:'none',border:'none',cursor:'pointer',fontSize:18,color:'#94a3b8'}}>✕</button>
        </div>
        <div style={{flex:1,overflowY:'auto',padding:'16px 20px',display:'flex',flexDirection:'column',gap:14}}>
          <div style={G2}>
            <div><label style={LS}>Full Name *</label><input style={IS} value={form.name||''} onChange={e=>s('name',e.target.value)} placeholder="e.g. Rajesh Kumar"/></div>
            <div><label style={LS}>Email *</label><input type="email" style={IS} value={form.email||''} onChange={e=>s('email',e.target.value)}/></div>
          </div>
          {isNew && (
            <div><label style={LS}>Password *</label><input type="password" style={IS} value={form.password||''} onChange={e=>s('password',e.target.value)} placeholder="Set initial password"/></div>
          )}
          <div style={G2}>
            <div>
              <label style={LS}>Designation</label>
              <select style={IS} value={form.designation||''} onChange={e=>s('designation',e.target.value)}>
                <option value="">— Select Designation —</option>
                {desigOpts.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label style={LS}>Role</label>
              <select style={IS} value={form.role_id||''} onChange={e=>s('role_id',e.target.value)}>
                <option value="">— Select Role —</option>
                {roleOpts.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div><label style={LS}>Department</label><input style={IS} value={form.department||''} onChange={e=>s('department',e.target.value)}/></div>
            <div><label style={LS}>Functional Area</label><input style={IS} value={form.functional_area||''} onChange={e=>s('functional_area',e.target.value)}/></div>
            <div><label style={LS}>Employee ID</label><input style={IS} value={form.employee_id||''} onChange={e=>s('employee_id',e.target.value)}/></div>
            <div><label style={LS}>Mobile</label><input type="tel" style={IS} value={form.phone||''} onChange={e=>s('phone',e.target.value)}/></div>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            <input type="checkbox" id="usr_active" checked={form.is_active!==false} onChange={e=>s('is_active',e.target.checked)} style={{width:15,height:15,cursor:'pointer'}}/>
            <label htmlFor="usr_active" style={{fontSize:13,color:'#374151',cursor:'pointer'}}>Active</label>
          </div>
        </div>
        <div style={{padding:'12px 20px',borderTop:'1px solid #e2e8f0',display:'flex',justifyContent:'flex-end',gap:10}}>
          <button onClick={()=>setModal(null)} style={{padding:'8px 18px',border:'1px solid #cbd5e1',borderRadius:7,background:'#fff',fontSize:13,cursor:'pointer'}}>Cancel</button>
          <button onClick={()=>save(form)} disabled={saving} style={{padding:'8px 20px',border:'none',borderRadius:7,background:saving?'#93c5fd':'#1a56db',color:'#fff',fontSize:13,fontWeight:600,cursor:saving?'not-allowed':'pointer'}}>
            {saving?'Saving…':'✓ Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── SuccessionPage ──────────────────────────────────────────── */
function SuccessionPage() {
  const [data,    setData]    = useState([]);
  const [modal,   setModal]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);

  const load = () => {
    setLoading(true);
    userMastersAPI.listSuccessions()
      .then(r => setData(r.data||[]))
      .catch(()=>{})
      .finally(()=>setLoading(false));
  };
  useEffect(load, []);

  const save = async (form) => {
    if (!form.separated_name?.trim() || !form.new_employee_name?.trim()) {
      toast.error('Both employee names are required'); return;
    }
    setSaving(true);
    try {
      const p = {
        separated_name:    form.separated_name.trim(),
        new_employee_name: form.new_employee_name.trim(),
        remark:            form.remark||null,
      };
      if (modal?.id) await userMastersAPI.updateSuccession(modal.id, p);
      else           await userMastersAPI.createSuccession(p);
      toast.success(modal?.id ? 'Record updated' : 'Succession created');
      setModal(null); load();
    } catch(e) { toast.error(e?.response?.data?.detail||'Save failed'); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this succession record?')) return;
    try { await userMastersAPI.deleteSuccession(id); toast.success('Deleted'); load(); }
    catch { toast.error('Failed'); }
  };

  return (
    <UMGrid
      cols={[
        {key:'separated_name',    label:'Separated Employee', render:r=><span className={styles.user_link}>{r.separated_name}</span>},
        {key:'new_employee_name', label:'New Employee'},
        {key:'remark',            label:'Remark'},
        {key:'created_by_name',   label:'Created By'},
        {key:'created_at',        label:'Date', render:r=>r.created_at?.slice?.(0,10)||''},
        {label:'Options', width:100, render:r=><RowActions onEdit={()=>setModal(r)} onDelete={()=>del(r.id)}/>},
      ]}
      rows={data} loading={loading} addLabel="Employee Succession" onAdd={()=>setModal({})} onRefresh={load}>
      {modal !== null && (
        <Modal title={modal?.id?'Edit Succession':'Add Employee Succession'}
          fields={[
            {key:'separated_name',    label:'Separated Employee *', required:true, placeholder:'Full name of leaving employee'},
            {key:'new_employee_name', label:'New Employee *',       required:true, placeholder:'Full name of replacement'},
            {key:'remark',            label:'Remark', type:'select', options:['Replace','Promote','Transfer','Lateral']},
          ]}
          initial={modal} onClose={()=>setModal(null)} onSave={save}/>
      )}
    </UMGrid>
  );
}

function AuthLevelPage() {
  const [data,  setData]  = useState(AUTH_LEVELS);
  const [modal, setModal] = useState(null);
  const [docF,  setDocF]  = useState('');
  const [locF,  setLocF]  = useState('');
  const docs     = [...new Set(data.map(d=>d.document))];
  const locs     = [...new Set(data.map(d=>d.location))];
  const filtered = data.filter(d=>(!docF||d.document===docF)&&(!locF||d.location===locF));
  const del  = id   => { if(window.confirm('Delete?')) { setData(d=>d.filter(x=>x.id!==id)); toast.success('Deleted'); }};
  const save = form => {
    if(modal==='add') setData(d=>[...d,{id:Date.now(),...form}]);
    else setData(d=>d.map(x=>x.id===modal.id?{...x,...form}:x));
    toast.success('Saved'); setModal(null);
  };
  const cols = [
    { key:'code',     label:'Auth Level Code' },
    { key:'name',     label:'Auth Level Name' },
    { key:'document', label:'Document Name'   },
    { key:'location', label:'Location Name'   },
    { label:'Options', width:100, render:r=><RowActions onEdit={()=>setModal(r)} onDelete={()=>del(r.id)}/> },
  ];
  return (
    <div style={{display:'flex',flexDirection:'column',height:'100%'}}>
      <div className={styles.page_bar}>
        <button className={styles.back_btn}>←</button>
        <h1 className={styles.page_title}>Authorization</h1>
        <button className={styles.add_btn} onClick={()=>setModal('add')}>＋ New Auth Level</button>
      </div>
      <div className={styles.filter_bar}>
        <span className={styles.filter_label}>Filter by</span>
        <select className={styles.filter_select} value={docF} onChange={e=>setDocF(e.target.value)}>
          <option value="">Document Modules</option>
          {docs.map(d=><option key={d} value={d}>{d}</option>)}
        </select>
        <select className={styles.filter_select} value={locF} onChange={e=>setLocF(e.target.value)}>
          <option value="">Location</option>
          {locs.map(l=><option key={l} value={l}>{l}</option>)}
        </select>
        {(docF||locF)&&<button className={styles.outline_btn} onClick={()=>{setDocF('');setLocF('');}}>✕ Clear</button>}
      </div>
      <MasterTable columns={cols} data={filtered} searchKeys={['code','name','document','location']} defaultPerPage={25} onRefresh={()=>toast.success('Refreshed')}/>
      {modal&&<Modal title={modal==='add'?'New Auth Level':'Edit Auth Level'} fields={[{key:'code',label:'Auth Level Code',placeholder:'0'},{key:'name',label:'Auth Level Name',placeholder:'e.g. Created By'},{key:'document',label:'Document Name',placeholder:'e.g. Purchase'},{key:'location',label:'Location Name',placeholder:'e.g. Chakan 2'}]} initial={modal==='add'?{}:modal} onClose={()=>setModal(null)} onSave={save}/>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// PROFILE MODAL
// ─────────────────────────────────────────────────────────────
const IMG_BASE_PROFILE = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace('/api', '');

export function ProfileModal({ user, mode, onClose, onSave }) {
  const [form,   setForm]   = useState({
    name:            user?.name            || '',
    email:           user?.email           || '',
    department:      user?.department      || '',
    phone:           user?.phone           || '',
    designation:     user?.designation     || '',
    functional_area: user?.functional_area || '',
    old_password:    '',
    new_password:    '',
    confirm_password:'',
  });
  const [saving,      setSaving]      = useState(false);
  const [photoFile,   setPhotoFile]   = useState(null);       // File object
  const [photoPreview,setPhotoPreview]= useState(             // preview src
    user?.avatar ? `${IMG_BASE_PROFILE}/uploads/avatars/${user.avatar}` : null
  );
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = useRef(null);

  const set      = (k,v) => setForm(f=>({...f,[k]:v}));
  const initials = form.name?.split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase()||'U';

  // Handle photo file selection — show preview immediately
  const onPhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    if (file.size > 5 * 1024 * 1024)    { toast.error('Image must be under 5 MB'); return; }
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  // Upload photo separately (instant save)
  const uploadPhoto = async () => {
    if (!photoFile) return;
    setUploadingPhoto(true);
    try {
      const r = await profileAPI.uploadPhoto(photoFile);
      const avatarUrl = `${IMG_BASE_PROFILE}/uploads/avatars/${r.data.avatar}`;
      setPhotoPreview(avatarUrl);
      setPhotoFile(null);
      onSave({ ...user, avatar: r.data.avatar });
      toast.success('Photo updated!');
    } catch {
      // Fallback — keep local preview, will save with profile
      toast.success('Photo ready — click Save Changes to apply');
    } finally { setUploadingPhoto(false); }
  };

  const submit = async () => {
    setSaving(true);
    try {
      if (mode==='password') {
        if (!form.old_password)                            { toast.error('Enter current password'); setSaving(false); return; }
        if (form.new_password.length < 6)                 { toast.error('Password must be ≥ 6 characters'); setSaving(false); return; }
        if (form.new_password !== form.confirm_password)  { toast.error('Passwords do not match'); setSaving(false); return; }
        try {
          await profileAPI.changePassword({ old_password:form.old_password, new_password:form.new_password });
        } catch (err) {
          toast.error(err?.response?.data?.detail || 'Current password is incorrect');
          setSaving(false); return;
        }
        toast.success('Password changed successfully');
        onClose();
      } else {
        // If there's a pending photo, upload it first
        let avatarVal = user?.avatar;
        if (photoFile) {
          try {
            const r = await profileAPI.uploadPhoto(photoFile);
            avatarVal = r.data.avatar;
          } catch { /* ignore photo error, still save profile */ }
        }
        const payload = {
          name: form.name, phone: form.phone, department: form.department,
          designation: form.designation, functional_area: form.functional_area,
        };
        try {
          const r = await profileAPI.update(payload);
          onSave({ ...user, ...r.data, avatar: avatarVal });
        } catch {
          onSave({ ...user, ...payload, avatar: avatarVal });
        }
        toast.success('Profile updated');
      }
    } finally { setSaving(false); }
  };

  // Avatar display — photo or initials circle
  const AvatarDisplay = ({ size = 76, fontSize = 24 }) => (
    photoPreview
      ? <img src={photoPreview} alt="Avatar"
          style={{ width:224, height:100, borderRadius:'50%', objectFit:'cover',
            flexShrink:0, boxShadow:'0 4px 14px rgba(0,0,0,.15)', border:'3px solid #fff' }}/>
      : <div style={{ width:size, height:size, borderRadius:'50%',
          background:'linear-gradient(135deg,#1a56db,#6366f1)',
          display:'flex', alignItems:'center', justifyContent:'center',
          fontSize, fontWeight:800, color:'#fff', flexShrink:0,
          boxShadow:'0 4px 14px rgba(26,86,219,.3)' }}>
          {initials}
        </div>
  );

  return (
    <div className={styles.overlay}>
      <div className={styles.modal} style={{width:520}} onClick={e=>e.stopPropagation()}>
        <div className={styles.modal_head}>
          <h2>{mode==='view'?'My Profile':mode==='edit'?'Edit Profile':'Change Password'}</h2>
          <button className={styles.modal_x} onClick={onClose}>✕</button>
        </div>

        <div className={styles.modal_body}>
          {mode !== 'password' ? (
            <div>
              {/* ── Avatar + Name header ── */}
              <div style={{display:'flex',alignItems:'center',gap:18,padding:'4px 0 20px',
                borderBottom:'1px solid #f1f5f9',marginBottom:18}}>

                {/* Avatar with edit overlay in edit mode */}
                <div style={{position:'relative',flexShrink:0}}>
                  <AvatarDisplay size={76} fontSize={26}/>
                  {mode==='edit' && (
                    <div
                      onClick={() => photoInputRef.current?.click()}
                      style={{
                        position:'absolute', inset:0, borderRadius:'50%',
                        background:'rgba(0,0,0,.45)', display:'flex', flexDirection:'column',
                        alignItems:'center', justifyContent:'center', cursor:'pointer',
                        opacity: photoFile ? 1 : 0, transition:'opacity .2s',
                      }}
                      onMouseEnter={e=>e.currentTarget.style.opacity=1}
                      onMouseLeave={e=>e.currentTarget.style.opacity= photoFile ? 1 : 0}
                    >
                      <span style={{fontSize:18}}>📷</span>
                      <span style={{fontSize:9,color:'#fff',fontWeight:600,marginTop:2}}>CHANGE</span>
                    </div>
                  )}
                  {/* Green tick when new photo selected */}
                  {photoFile && (
                    <div style={{position:'absolute',bottom:0,right:0,width:22,height:22,borderRadius:'50%',
                      background:'#16a34a',display:'flex',alignItems:'center',justifyContent:'center',
                      border:'2px solid #fff',fontSize:11,color:'#fff',fontWeight:700}}>✓</div>
                  )}
                </div>

                {/* Hidden file input */}
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  style={{display:'none'}}
                  onChange={onPhotoChange}
                />

                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:18,fontWeight:700,color:'#1e2a3b'}}>{form.name||'—'}</div>
                  <div style={{fontSize:12,color:'#94a3b8',marginTop:3}}>
                    {user?.role_name||'User'} · {user?.org_name||''}
                  </div>
                  {mode==='edit' && (
                    <div style={{display:'flex',gap:8,marginTop:10,alignItems:'center',flexWrap:'wrap'}}>
                      <button
                        onClick={() => photoInputRef.current?.click()}
                        style={{
                          display:'inline-flex',alignItems:'center',gap:6,
                          padding:'5px 12px',borderRadius:6,border:'1px solid #d1d5db',
                          background:'#fff',cursor:'pointer',fontSize:12,fontWeight:600,
                          color:'#374151',fontFamily:'inherit',
                        }}>
                        📷 Change Photo
                      </button>
                      {photoFile && (
                        <button
                          onClick={uploadPhoto}
                          disabled={uploadingPhoto}
                          style={{
                            display:'inline-flex',alignItems:'center',gap:6,
                            padding:'5px 12px',borderRadius:6,border:'none',
                            background:'#16a34a',cursor:'pointer',fontSize:12,fontWeight:600,
                            color:'#fff',fontFamily:'inherit',
                          }}>
                          {uploadingPhoto ? '⏳ Uploading…' : '⬆ Upload Now'}
                        </button>
                      )}
                      {photoFile && (
                        <span style={{fontSize:11,color:'#6b7280'}}>
                          {photoFile.name} ({(photoFile.size/1024).toFixed(0)} KB)
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* ── Form fields ── */}
              <div className={styles.form_grid}>
                {[
                  ['name','Full Name',true],
                  ['email','Email Address',false],
                  ['phone','Phone',true],
                  ['department','Department',true],
                  ['designation','Designation',true],
                  ['functional_area','Functional Area',true],
                ].map(([k,l,editable])=>(
                  <div key={k} className={styles.fg}>
                    <label>{l}</label>
                    {(editable && mode==='edit')
                      ? <input value={form[k]} onChange={e=>set(k,e.target.value)} placeholder={`Enter ${l}`}/>
                      : <div style={{padding:'8px 0',fontSize:13,
                          color:k==='name'?'#1e2a3b':'#64748b',
                          fontWeight:k==='name'?600:400}}>{form[k]||'—'}</div>
                    }
                  </div>
                ))}
                <div className={styles.fg}>
                  <label>Role</label>
                  <div style={{padding:'8px 0',fontSize:13,color:'#64748b'}}>{user?.role_name||'—'}</div>
                </div>
                <div className={styles.fg}>
                  <label>Organisation</label>
                  <div style={{padding:'8px 0',fontSize:13,color:'#64748b'}}>{user?.org_name||'—'}</div>
                </div>
              </div>
            </div>
          ) : (
            /* ── Change Password ── */
            <div style={{display:'flex',flexDirection:'column',gap:16}}>
              <div style={{background:'#eff4ff',border:'1px solid #c7d4f0',borderRadius:8,
                padding:'12px 14px',fontSize:13,color:'#1a56db'}}>
                🔒 Password must be at least 6 characters long.
              </div>
              <div className={styles.fg}>
                <label>Current Password *</label>
                <input type="password" value={form.old_password}
                  onChange={e=>set('old_password',e.target.value)} placeholder="Enter current password"/>
              </div>
              <div className={styles.fg}>
                <label>New Password *</label>
                <input type="password" value={form.new_password}
                  onChange={e=>set('new_password',e.target.value)} placeholder="Enter new password"/>
              </div>
              <div className={styles.fg}>
                <label>Confirm New Password *</label>
                <input type="password" value={form.confirm_password}
                  onChange={e=>set('confirm_password',e.target.value)} placeholder="Re-enter new password"/>
              </div>
              {form.new_password && (
                <div>
                  <div style={{display:'flex',gap:4,marginBottom:4}}>
                    {[1,2,3,4].map(i=>(
                      <div key={i} style={{flex:1,height:4,borderRadius:4,background:
                        form.new_password.length>=8&&/[A-Z]/.test(form.new_password)&&/[0-9]/.test(form.new_password)&&/[^A-Za-z0-9]/.test(form.new_password)?'#22c55e':
                        form.new_password.length>=8&&(/[A-Z]/.test(form.new_password)||/[0-9]/.test(form.new_password))?'#f59e0b':
                        form.new_password.length>=6?'#f59e0b':'#ef4444'
                      }}/>
                    ))}
                  </div>
                  <div style={{fontSize:11,color:'#64748b'}}>
                    {form.new_password.length<6?'Too short':
                     form.new_password.length>=8&&/[A-Z]/.test(form.new_password)&&/[0-9]/.test(form.new_password)?
                     'Strong password ✓':'Add uppercase & numbers for a stronger password'}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className={styles.modal_foot}>
          <button className={styles.btn_cancel} onClick={onClose}>
            {mode==='view' ? 'Close' : 'Cancel'}
          </button>
          {mode!=='view' && (
            <button className={styles.btn_save} onClick={submit} disabled={saving}>
              {saving ? 'Saving…' : mode==='edit' ? 'Save Changes' : 'Change Password'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// ASSET MASTERS — Category, Employee, Method, Dep Type
// ═══════════════════════════════════════════════════════════════

/* ── Shared CRUD table for asset masters ───────────────────── */
function AMasterGrid({ title, icon='📋', columns, rows=[], loading=false, onAdd, onEdit, onDelete }) {
  const [page,    setPage]    = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [search,  setSearch]  = useState('');

  useEffect(() => { setPage(1); }, [rows.length, search]);

  const filtered = search
    ? rows.filter(r => Object.values(r).some(v => String(v||'').toLowerCase().includes(search.toLowerCase())))
    : rows;

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const safePage   = Math.min(page, totalPages);
  const paged      = filtered.slice((safePage-1)*perPage, safePage*perPage);

  const CS = {
    wrap:   { border:'1px solid #e2e8f0', borderRadius:10, overflow:'hidden' },
    thead:  { background:'#f8fafc' },
    th:     { padding:'10px 14px', textAlign:'left', fontSize:11, fontWeight:700,
              textTransform:'uppercase', letterSpacing:'.05em', color:'#94a3b8',
              borderBottom:'1px solid #e2e8f0', whiteSpace:'nowrap' },
    td:     { padding:'11px 14px', fontSize:13, color:'#1a1f2e', borderBottom:'1px solid #f1f5f9' },
    actBtn: { padding:'4px 10px', borderRadius:6, fontSize:11, cursor:'pointer', fontWeight:500 },
  };
  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12, flexWrap:'wrap', gap:8 }}>
        <h2 style={{ margin:0, fontSize:18, fontWeight:700, color:'#1a1f2e' }}>{icon} {title}</h2>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          <input value={search} onChange={e=>setSearch(e.target.value)}
            placeholder="Search…"
            style={{ border:'1.5px solid #e2e8f0', borderRadius:7, padding:'6px 10px',
              fontSize:12, outline:'none', width:160 }}/>
          <select value={perPage} onChange={e=>{setPerPage(Number(e.target.value));setPage(1);}}
            style={{ border:'1.5px solid #e2e8f0', borderRadius:7, padding:'6px 8px',
              fontSize:12, outline:'none', background:'#fff' }}>
            {[10,25,50,100].map(n=><option key={n} value={n}>{n} / page</option>)}
          </select>
          <button onClick={onAdd} style={{ padding:'8px 16px', background:'#1a56db', color:'#fff',
            border:'none', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer',
            whiteSpace:'nowrap' }}>+ Add New</button>
        </div>
      </div>

      {/* Scrollable table */}
      <div style={{ ...CS.wrap, maxHeight:'60vh', overflowY:'auto' }}>
        {loading
          ? <div style={{ padding:40, textAlign:'center', color:'#94a3b8' }}>Loading…</div>
          : filtered.length === 0
            ? <div style={{ padding:40, textAlign:'center', color:'#94a3b8' }}>
                {search ? `No results for "${search}"` : 'No records found. Click + Add New.'}
              </div>
            : (
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead style={CS.thead}>
                  <tr>
                    {columns.map(col=><th key={col.key} style={CS.th}>{col.label}</th>)}
                    <th style={{ ...CS.th, textAlign:'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paged.map((row,i)=>(
                    <tr key={row.id||i} style={{ background: i%2===0?'#fff':'#fafbfc' }}>
                      {columns.map(col=>(
                        <td key={col.key} style={{ ...CS.td,
                          ...(col.mono?{fontFamily:'monospace',fontWeight:700}:{}),
                          ...(col.bold?{fontWeight:700}:{}) }}>
                          {col.render ? col.render(row) : String(row[col.key]??'')}
                        </td>
                      ))}
                      <td style={{ ...CS.td, textAlign:'right', whiteSpace:'nowrap' }}>
                        <button onClick={()=>onEdit(row)}
                          style={{ ...CS.actBtn, border:'1px solid #e2e8f0',
                            background:'#f8fafc', marginRight:6, color:'#374151' }}>✏ Edit</button>
                        <button onClick={()=>onDelete(row.id)}
                          style={{ ...CS.actBtn, border:'1px solid #fecaca',
                            background:'#fff', color:'#dc2626' }}>🗑</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
        }
      </div>

      {/* Pagination */}
      {!loading && filtered.length > perPage && (
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
          padding:'10px 4px', marginTop:6, flexWrap:'wrap', gap:8 }}>
          <span style={{ fontSize:12, color:'#64748b' }}>
            {Math.min((safePage-1)*perPage+1, filtered.length)}–{Math.min(safePage*perPage, filtered.length)} of {filtered.length}
            {search ? ` (from ${rows.length} total)` : ''}
          </span>
          <div style={{ display:'flex', gap:4 }}>
            <button onClick={()=>setPage(1)} disabled={safePage===1}
              style={{ padding:'4px 8px', border:'1px solid #e2e8f0', borderRadius:6,
                fontSize:12, cursor:safePage===1?'default':'pointer', background:'#fff',
                color:safePage===1?'#d1d5db':'#374151' }}>«</button>
            <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={safePage===1}
              style={{ padding:'4px 10px', border:'1px solid #e2e8f0', borderRadius:6,
                fontSize:12, cursor:safePage===1?'default':'pointer', background:'#fff',
                color:safePage===1?'#d1d5db':'#374151' }}>‹ Prev</button>
            {Array.from({length:Math.min(5,totalPages)},(_,i)=>{
              const s = Math.max(1, Math.min(safePage-2, totalPages-4));
              return s+i;
            }).map(n=>(
              <button key={n} onClick={()=>setPage(n)}
                style={{ padding:'4px 9px', borderRadius:6, fontSize:12, cursor:'pointer',
                  border:`1.5px solid ${safePage===n?'#1a56db':'#e2e8f0'}`,
                  background:safePage===n?'#1a56db':'#fff',
                  color:safePage===n?'#fff':'#374151', fontWeight:safePage===n?700:400 }}>{n}</button>
            ))}
            <button onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={safePage===totalPages}
              style={{ padding:'4px 10px', border:'1px solid #e2e8f0', borderRadius:6,
                fontSize:12, cursor:safePage===totalPages?'default':'pointer', background:'#fff',
                color:safePage===totalPages?'#d1d5db':'#374151' }}>Next ›</button>
            <button onClick={()=>setPage(totalPages)} disabled={safePage===totalPages}
              style={{ padding:'4px 8px', border:'1px solid #e2e8f0', borderRadius:6,
                fontSize:12, cursor:safePage===totalPages?'default':'pointer', background:'#fff',
                color:safePage===totalPages?'#d1d5db':'#374151' }}>»</button>
          </div>
        </div>
       )}
    </div>
  );
}


function AMasterModal({ title, fields, data={}, onSave, onClose, saving=false }) {
  const [form, setForm] = useState({ is_active:true, ...data });
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const IS2 = { width:'100%', border:'1.5px solid #e2e8f0', borderRadius:7,
    padding:'8px 11px', fontSize:13, outline:'none', boxSizing:'border-box', background:'#fff' };
  const LS2 = { fontSize:12, fontWeight:600, color:'#475569', display:'block', marginBottom:4 };
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(10,14,26,.55)', backdropFilter:'blur(4px)',
      zIndex:1200, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
      <div style={{ background:'#fff', borderRadius:12, width:520, maxWidth:'96vw', maxHeight:'90vh',
        display:'flex', flexDirection:'column', boxShadow:'0 24px 64px rgba(0,0,0,.2)', overflow:'hidden' }}>
        <div style={{ padding:'16px 20px 12px', borderBottom:'1px solid #e2e8f0',
          display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ fontSize:16, fontWeight:700, color:'#1a1f2e' }}>{title}</div>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', fontSize:18, color:'#94a3b8' }}>✕</button>
        </div>
        <div style={{ flex:1, overflowY:'auto', padding:'16px 20px', display:'flex', flexDirection:'column', gap:14 }}>
          {fields.map(f=>(
            <div key={f.key}>
              <label style={LS2}>{f.label}{f.required?' *':''}</label>
              {f.type==='select' ? (
                <select style={IS2} value={form[f.key]??''} onChange={e=>set(f.key,e.target.value)}>
                  <option value="">— Select —</option>
                  {(f.options||[]).map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              ) : f.type==='textarea' ? (
                <textarea style={{ ...IS2, resize:'vertical', minHeight:70, fontFamily:'inherit' }}
                  value={form[f.key]??''} onChange={e=>set(f.key,e.target.value)} placeholder={f.placeholder||''}/>
              ) : f.type==='checkbox' ? (
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <input type="checkbox" id={`am_${f.key}`} checked={!!form[f.key]}
                    onChange={e=>set(f.key,e.target.checked)} style={{ width:15,height:15,cursor:'pointer' }}/>
                  <label htmlFor={`am_${f.key}`} style={{ fontSize:13,color:'#374151',cursor:'pointer' }}>{f.checkLabel||'Active'}</label>
                </div>
              ) : (
                <input type={f.type||'text'} style={IS2} value={form[f.key]??''}
                  onChange={e=>set(f.key,e.target.value)} placeholder={f.placeholder||''}/>
              )}
              {f.hint && <div style={{ fontSize:11,color:'#94a3b8',marginTop:3 }}>{f.hint}</div>}
            </div>
          ))}
        </div>
        <div style={{ padding:'12px 20px', borderTop:'1px solid #e2e8f0', display:'flex', justifyContent:'flex-end', gap:10 }}>
          <button onClick={onClose} style={{ padding:'8px 18px', border:'1px solid #cbd5e1', borderRadius:7, background:'#fff', fontSize:13, cursor:'pointer' }}>Cancel</button>
          <button onClick={()=>onSave(form)} disabled={saving}
            style={{ padding:'8px 20px', border:'none', borderRadius:7, background:saving?'#93c5fd':'#1a56db',
              color:'#fff', fontSize:13, fontWeight:600, cursor:saving?'not-allowed':'pointer' }}>
            {saving?'Saving…':'✓ Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Category Master Page ─────────────────────────────────────
function CategoryMasterPage() {
  const [rows,setRows]=useState([]); const [loading,setLoading]=useState(true);
  const [modal,setModal]=useState(null); const [saving,setSaving]=useState(false);
  const load=()=>{ setLoading(true); mastersAPI.listCategories().then(r=>setRows(r.data||[])).catch(()=>{}).finally(()=>setLoading(false)); };
  useEffect(load,[]);
  const save=async(form)=>{
    if(!form.name||!form.name.trim()){toast.error('Category Name is required');return;}
    setSaving(true);
    try {
      // code is optional — only send if provided
      const p={
        name:form.name.trim(),
        code:form.code?form.code.trim().toUpperCase():form.name.trim().substring(0,8).toUpperCase().replace(/\s+/g,'_'),
        description:form.description||null,
        is_active:form.is_active!==false
      };
      if(modal.id) await mastersAPI.updateCategory(modal.id,p); else await mastersAPI.createCategory(p);
      toast.success(modal.id?'Category updated':'Category created'); setModal(null); load();
    } catch(e){toast.error(e?.response?.data?.detail||'Save failed');} finally{setSaving(false);}
  };
  const del=async(id)=>{
    if(!window.confirm('Deactivate this category?'))return;
    try{await mastersAPI.deleteCategory(id);toast.success('Deactivated');load();}catch{toast.error('Failed');}
  };
  const cols=[
    {key:'code',label:'Code',mono:true,bold:true,render:r=>r.code||<span style={{color:'#cbd5e1',fontSize:11}}>—</span>},
    {key:'name',label:'Category Name'},
    {key:'description',label:'Description',render:r=>r.description||<span style={{color:'#cbd5e1',fontSize:11}}>—</span>},
    {key:'is_active',label:'Active',render:r=>r.is_active||r.is_active===undefined?'✅ Yes':'❌ No'},
  ];
  const fields=[
    {key:'name',label:'Category Name *',placeholder:'e.g. Mechanical Equipment',required:true},
    {key:'code',label:'Category Code',placeholder:'e.g. MECH (auto-generated if blank)',hint:'Short uppercase code. Leave blank to auto-generate from name.'},
    {key:'description',label:'Description',type:'textarea',placeholder:'Optional description'},
    {key:'is_active',label:'Status',type:'checkbox'},
  ];
  return (<>
    <AMasterGrid title="Category Master" icon="🗂" columns={cols} rows={rows} loading={loading} onAdd={()=>setModal({is_active:true})} onEdit={r=>setModal(r)} onDelete={del}/>
    {modal&&<AMasterModal title={modal.id?'Edit Category':'Add Category'} fields={fields} data={modal} onSave={save} onClose={()=>setModal(null)} saving={saving}/>}
  </>);
}

// ── Employee Master Page (with Designation/Dept/Location dropdowns) ───────────────
function EmployeeMasterPage() {
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [q,       setQ]       = useState('');

  /* dropdown options for modal */
  const [designations, setDesignations] = useState([]);
  const [departments,  setDepartments]  = useState([]);
  const [locations,    setLocations]    = useState([]);

  const loadOptions = () => {
    userMastersAPI.listDesignations().then(r => setDesignations(r.data||[])).catch(()=>{
      mastersAPI.lookupDesignations().then(r => setDesignations(r.data||[])).catch(()=>{});
    });
    /* Departments — try companyAPI first (same source as Department Master page),
       fallback to mastersAPI.lookupDepartments */
    companyAPI.listDepartments()
      .then(r => {
        const data = r.data?.data || r.data || [];
        setDepartments(data.filter(d => d.is_active !== false));
      })
      .catch(() => {
        mastersAPI.lookupDepartments()
          .then(r => setDepartments(r.data||[]))
          .catch(()=>{});
      });
    mastersAPI.listLocations().then(r => setLocations(r.data||[])).catch(()=>{});
  };

  const load = (search) => {
    setLoading(true);
    mastersAPI.listEmployees(search||undefined)
      .then(r => setRows(r.data||[]))
      .catch(()=>{})
      .finally(()=>setLoading(false));
  };

  useEffect(() => { load(); loadOptions(); }, []);

  const save = async (form) => {
    if (!form.emp_code || !form.name) { toast.error('Code and Name required'); return; }
    setSaving(true);
    try {
      const p = {
        emp_code:    form.emp_code,
        name:        form.name,
        designation: form.designation||null,
        department:  form.department||null,
        location:    form.location||null,
        email:       form.email||null,
        mobile:      form.mobile||null,
        is_active:   form.is_active !== false,
      };
      if (modal.id) await mastersAPI.updateEmployee(modal.id, p);
      else          await mastersAPI.createEmployee(p);
      toast.success(modal.id ? 'Employee updated' : 'Employee created');
      setModal(null);
      load(q||undefined);
    } catch(e) { toast.error(e?.response?.data?.detail||'Save failed'); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Deactivate this employee?')) return;
    try { await mastersAPI.deleteEmployee(id); toast.success('Deactivated'); load(); }
    catch { toast.error('Failed'); }
  };

  const cols = [
    {key:'emp_code',    label:'Code',        mono:true, bold:true},
    {key:'name',        label:'Full Name'},
    {key:'designation', label:'Designation'},
    {key:'department',  label:'Department'},
    {key:'location',    label:'Location'},
    {key:'mobile',      label:'Mobile'},
    {key:'is_active',   label:'Active', render:r=>r.is_active?'✅':'❌'},
  ];

  /* Build dynamic fields for the modal — desig/dept/loc are selects with live options */

  return (
    <>
      <div style={{display:'flex',gap:8,marginBottom:14,alignItems:'center'}}>
        <input value={q} onChange={e=>setQ(e.target.value)}
          onKeyDown={e=>e.key==='Enter'&&load(q)}
          placeholder="Search employees…"
          style={{border:'1.5px solid #e2e8f0',borderRadius:8,padding:'7px 12px',fontSize:13,outline:'none',width:240}}/>
        <button onClick={()=>load(q)}
          style={{padding:'7px 14px',background:'#f1f5f9',border:'1px solid #e2e8f0',borderRadius:7,fontSize:13,cursor:'pointer'}}>
          Search
        </button>
      </div>

      <AMasterGrid
        title="Employee Master" icon="👤"
        columns={cols} rows={rows} loading={loading}
        onAdd={()=>setModal({is_active:true})}
        onEdit={r=>setModal(r)}
        onDelete={del}/>

      {/* Custom Employee Modal with dropdowns */}
      {modal && (
        <EmployeeModal
          employee={modal}
          onSave={save}
          onClose={() => setModal(null)}
          saving={saving}
          designations={designations}
          departments={departments}
          locations={locations}
        />
      )}

    </>
  );
}

/* Custom modal for Employee with select dropdowns for Designation/Dept/Location */
function EmployeeModal({ employee, onSave, onClose, saving,
  designations = [], departments = [], locations = [] }) {
  const isEdit = Boolean(employee?.id);

  const [form, setForm] = useState({
    emp_code:    employee?.emp_code    || '',
    name:        employee?.name        || '',
    designation: employee?.designation || '',
    department:  employee?.department  || '',
    location:    employee?.location    || '',
    email:       employee?.email       || '',
    mobile:      employee?.mobile      || '',
    is_active:   employee?.is_active !== false,
  });

  // Login / password state
  const [loginStatus,   setLoginStatus]   = useState(null);
  const [loginLoading,  setLoginLoading]  = useState(false);
  const [password,      setPassword]      = useState('');
  const [confirmPwd,    setConfirmPwd]    = useState('');
  const [showPwd,       setShowPwd]       = useState(false);
  const [selectedRole,  setSelectedRole]  = useState('');
  const [savingPwd,     setSavingPwd]     = useState(false);
  const [removingLogin, setRemovingLogin] = useState(false);

  const sf = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Fetch login status when editing existing employee
  useEffect(() => {
    if (!isEdit) return;
    setLoginLoading(true);
    mastersAPI.getEmployeeLoginStatus(employee.id)
      .then(r => {
        setLoginStatus(r.data);
        if (r.data.role_id) setSelectedRole(String(r.data.role_id));
        else if (r.data.roles?.length) setSelectedRole(String(r.data.roles[r.data.roles.length - 1].id));
      })
      .catch(() => {})
      .finally(() => setLoginLoading(false));
  }, [employee.id]);

  const handleSetPassword = async () => {
    if (!password)              { toast.error('Enter a password');        return; }
    if (password.length < 6)    { toast.error('Minimum 6 characters');    return; }
    if (password !== confirmPwd){ toast.error('Passwords do not match');   return; }
    if (!form.email?.trim())    { toast.error('Save the employee with an email address first'); return; }
    setSavingPwd(true);
    try {
      const r = await mastersAPI.setEmployeePassword(employee.id, {
        password,
        confirm_password: confirmPwd,
        role_id: selectedRole ? parseInt(selectedRole) : null,
      });
      toast.success(r.data.message || 'Password saved');
      setPassword(''); setConfirmPwd('');
      mastersAPI.getEmployeeLoginStatus(employee.id)
        .then(res => setLoginStatus(res.data)).catch(() => {});
    } catch(e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally { setSavingPwd(false); }
  };

  const handleUpdateRole = async (roleId) => {
    setSelectedRole(roleId);
    if (!loginStatus?.has_login) return;   // only update if account exists
    try {
      await mastersAPI.updateEmployeeRole(employee.id, { role_id: parseInt(roleId) });
      toast.success('Permission level updated');
      mastersAPI.getEmployeeLoginStatus(employee.id)
        .then(res => setLoginStatus(res.data)).catch(() => {});
    } catch { toast.error('Failed to update role'); }
  };

  const handleRemoveLogin = async () => {
    if (!window.confirm('Remove login access for this employee?')) return;
    setRemovingLogin(true);
    try {
      const r = await mastersAPI.removeEmployeeLogin(employee.id);
      toast.success(r.data.message || 'Login removed');
      mastersAPI.getEmployeeLoginStatus(employee.id)
        .then(res => setLoginStatus(res.data)).catch(() => {});
    } catch(e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally { setRemovingLogin(false); }
  };

  const IS = {
    width:'100%', border:'1.5px solid #e2e8f0', borderRadius:7,
    padding:'8px 11px', fontSize:13, outline:'none',
    boxSizing:'border-box', background:'#fff',
  };
  const LS = { fontSize:12, fontWeight:600, color:'#475569', display:'block', marginBottom:4 };
  const G2 = { display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px 16px' };
  const SH = {
    fontSize:11, fontWeight:700, color:'rgba(26,31,46,.35)',
    textTransform:'uppercase', letterSpacing:'.07em',
    marginBottom:10, paddingBottom:5, borderBottom:'1px solid #f1f5f9',
  };

  return (
    <div style={{
      position:'fixed', inset:0,
      background:'rgba(10,14,26,.55)', backdropFilter:'blur(4px)',
      zIndex:1200, display:'flex', alignItems:'center', justifyContent:'center', padding:16,
    }}>
      <div style={{
        background:'#fff', borderRadius:14, width:640, maxWidth:'96vw',
        maxHeight:'92vh', display:'flex', flexDirection:'column',
        boxShadow:'0 24px 64px rgba(0,0,0,.22)', overflow:'hidden',
      }}>

        {/* Header */}
        <div style={{ padding:'16px 20px 12px', borderBottom:'1px solid #e2e8f0',
          display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ fontSize:16, fontWeight:700, color:'#1a1f2e' }}>
            {isEdit ? '✏ Edit Employee' : '👤 Add Employee'}
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none',
            cursor:'pointer', fontSize:20, color:'#94a3b8' }}>✕</button>
        </div>

        {/* Body */}
        <div style={{ flex:1, overflowY:'auto', padding:'18px 20px',
          display:'flex', flexDirection:'column', gap:20 }}>

          {/* ── Section 1: Employee Details ── */}
          <div>
            <div style={SH}>Employee Details</div>
            <div style={G2}>
              <div>
                <label style={LS}>Employee Code *</label>
                <input value={form.emp_code}
                  onChange={e => sf('emp_code', e.target.value.toUpperCase())}
                  placeholder="e.g. EMP-001"
                  style={{ ...IS, fontFamily:'monospace' }}/>
              </div>
              <div>
                <label style={LS}>Full Name *</label>
                <input value={form.name} onChange={e => sf('name', e.target.value)}
                  placeholder="e.g. Rajesh Kumar" style={IS}/>
              </div>
              <div>
                <label style={LS}>Designation</label>
                {designations.length > 0 ? (
                  <select value={form.designation} onChange={e => sf('designation', e.target.value)} style={IS}>
                    <option value="">— Select Designation —</option>
                    {designations.map(d => (
                      <option key={d.id || d.name} value={d.name}>{d.name}</option>
                    ))}
                  </select>
                ) : (
                  <input value={form.designation} onChange={e => sf('designation', e.target.value)}
                    placeholder="e.g. Maintenance Engineer" style={IS}/>
                )}
              </div>
              <div>
                <label style={LS}>Department</label>
                {departments.length > 0 ? (
                  <select value={form.department} onChange={e => sf('department', e.target.value)} style={IS}>
                    <option value="">— Select Department —</option>
                    {departments.map(d => (
                      <option key={d.id || d.name} value={d.name}>{d.name}</option>
                    ))}
                  </select>
                ) : (
                  <input value={form.department} onChange={e => sf('department', e.target.value)}
                    placeholder="e.g. Maintenance" style={IS}/>
                )}
              </div>
              <div>
                <label style={LS}>Location</label>
                {locations.length > 0 ? (
                  <select value={form.location} onChange={e => sf('location', e.target.value)} style={IS}>
                    <option value="">— Select Location —</option>
                    {locations.map(l => (
                      <option key={l.id} value={l.location_name || l.name}>{l.location_name || l.name}</option>
                    ))}
                  </select>
                ) : (
                  <input value={form.location} onChange={e => sf('location', e.target.value)}
                    placeholder="e.g. Plant A" style={IS}/>
                )}
              </div>
              <div>
                <label style={LS}>Mobile</label>
                <input type="tel" value={form.mobile} onChange={e => sf('mobile', e.target.value)}
                  placeholder="e.g. 9876543210" style={IS}/>
              </div>
              <div style={{ gridColumn:'1/-1' }}>
                <label style={LS}>Email Address (used as login username)</label>
                <input type="email" value={form.email} onChange={e => sf('email', e.target.value)}
                  placeholder="e.g. rajesh@company.com" style={IS}/>
                {form.email && (
                  <div style={{ fontSize:11, color:'#64748b', marginTop:3 }}>
                    💡 Employee logs in with this email address
                  </div>
                )}
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:8, paddingTop:6 }}>
                <input type="checkbox" id="emp_act"
                  checked={form.is_active !== false}
                  onChange={e => sf('is_active', e.target.checked)}
                  style={{ width:15, height:15, cursor:'pointer' }}/>
                <label htmlFor="emp_act" style={{ fontSize:13, color:'#374151', cursor:'pointer' }}>
                  Active
                </label>
              </div>
            </div>
          </div>

          {/* ── Section 2: App Login Access (edit mode only) ── */}
          {isEdit && (
            <div style={{ background:'#f8fafc', borderRadius:12,
              border:'1px solid #e2e8f0', padding:16 }}>
              <div style={SH}>🔐 App Login & Permissions</div>

              {/* Login status badge */}
              {loginLoading ? (
                <div style={{ fontSize:13, color:'#94a3b8', marginBottom:14 }}>
                  Checking login status…
                </div>
              ) : loginStatus && (
                <div style={{
                  marginBottom:14,
                  background: loginStatus.has_login && loginStatus.user_active !== false
                    ? '#f0fdf4' : '#f8fafc',
                  border:`1px solid ${loginStatus.has_login && loginStatus.user_active !== false
                    ? '#bbf7d0' : '#e2e8f0'}`,
                  borderRadius:8, padding:'10px 14px',
                  display:'flex', alignItems:'center',
                  justifyContent:'space-between', gap:12,
                }}>
                  <div>
                    {loginStatus.has_login && loginStatus.user_active !== false ? (
                      <>
                        <div style={{ fontSize:13, fontWeight:700, color:'#16a34a' }}>✅ Login Active</div>
                        <div style={{ fontSize:12, color:'#64748b', marginTop:2 }}>
                          Login: <strong>{loginStatus.email}</strong>
                          {loginStatus.role_name && <> · Role: <strong>{loginStatus.role_name}</strong></>}
                        </div>
                      </>
                    ) : loginStatus.has_login ? (
                      <>
                        <div style={{ fontSize:13, fontWeight:700, color:'#d97706' }}>⚠ Login Deactivated</div>
                        <div style={{ fontSize:12, color:'#64748b', marginTop:2 }}>Set a new password below to re-enable.</div>
                      </>
                    ) : (
                      <>
                        <div style={{ fontSize:13, fontWeight:600, color:'#94a3b8' }}>⚫ No Login Account</div>
                        <div style={{ fontSize:12, color:'#94a3b8', marginTop:2 }}>
                          {loginStatus.email ? 'Set a password below to enable login.' : 'Add an email address first, then save.'}
                        </div>
                      </>
                    )}
                  </div>
                  {loginStatus.has_login && loginStatus.user_active !== false && (
                    <button onClick={handleRemoveLogin} disabled={removingLogin}
                      style={{ padding:'5px 12px', background:'#fff', border:'1px solid #fca5a5',
                        borderRadius:6, fontSize:12, color:'#dc2626', cursor:'pointer', whiteSpace:'nowrap' }}>
                      {removingLogin ? 'Removing…' : '✕ Remove Login'}
                    </button>
                  )}
                </div>
              )}

              {/* Role / Permission dropdown */}
              {loginStatus?.roles?.length > 0 && (
                <div style={{ marginBottom:14 }}>
                  <label style={LS}>
                    Permission Level (Role)
                    {loginStatus.has_login && loginStatus.user_active !== false && (
                      <span style={{ color:'#94a3b8', fontWeight:400, marginLeft:6 }}>
                        — changes apply immediately
                      </span>
                    )}
                  </label>
                  <select value={selectedRole} onChange={e => handleUpdateRole(e.target.value)}
                    style={{ ...IS, width:'auto', minWidth:220 }}>
                    {loginStatus.roles.map(r => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </select>
                  <div style={{ fontSize:11, color:'#64748b', marginTop:3 }}>
                    This controls what the employee can see and do in the app.
                  </div>
                </div>
              )}

              {/* Password fields */}
              <div style={G2}>
                <div>
                  <label style={LS}>
                    {loginStatus?.has_login ? 'New Password' : 'Set Password'}
                  </label>
                  <div style={{ position:'relative' }}>
                    <input
                      type={showPwd ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="Min. 6 characters"
                      style={{ ...IS, paddingRight:36 }}
                    />
                    <button type="button" onClick={() => setShowPwd(v => !v)}
                      style={{ position:'absolute', right:8, top:'50%', transform:'translateY(-50%)',
                        background:'none', border:'none', cursor:'pointer', fontSize:14, color:'#94a3b8' }}>
                      {showPwd ? '🙈' : '👁'}
                    </button>
                  </div>
                </div>
                <div>
                  <label style={LS}>Confirm Password</label>
                  <input
                    type={showPwd ? 'text' : 'password'}
                    value={confirmPwd}
                    onChange={e => setConfirmPwd(e.target.value)}
                    placeholder="Re-enter password"
                    style={{
                      ...IS,
                      borderColor: confirmPwd && password !== confirmPwd ? '#fca5a5' : '#e2e8f0',
                    }}
                  />
                  {confirmPwd && password !== confirmPwd && (
                    <div style={{ fontSize:11, color:'#dc2626', marginTop:3 }}>Passwords do not match</div>
                  )}
                </div>
              </div>

              {/* Save password button */}
              <div style={{ display:'flex', justifyContent:'flex-end', marginTop:12 }}>
                <button
                  onClick={handleSetPassword}
                  disabled={savingPwd || !password || password !== confirmPwd}
                  style={{
                    padding:'8px 20px', borderRadius:7, border:'none',
                    background: (savingPwd || !password || password !== confirmPwd)
                      ? '#e2e8f0' : '#1a56db',
                    color: (savingPwd || !password || password !== confirmPwd)
                      ? '#94a3b8' : '#fff',
                    fontSize:13, fontWeight:600,
                    cursor: (savingPwd || !password || password !== confirmPwd)
                      ? 'not-allowed' : 'pointer',
                  }}>
                  {savingPwd
                    ? 'Saving…'
                    : loginStatus?.has_login
                    ? '🔑 Update Password'
                    : '🔐 Enable Login'}
                </button>
              </div>
            </div>
          )}

          {/* Hint for new employee */}
          {!isEdit && (
            <div style={{ background:'#f0f6ff', borderRadius:8, padding:'10px 14px',
              fontSize:12, color:'#1a56db' }}>
              💡 After saving this employee, click ✏ Edit to set a password and enable app login.
            </div>
          )}

        </div>

        {/* Footer */}
        <div style={{ padding:'12px 20px', borderTop:'1px solid #e2e8f0',
          display:'flex', justifyContent:'flex-end', gap:10 }}>
          <button onClick={onClose}
            style={{ padding:'8px 18px', border:'1px solid #e2e8f0', borderRadius:7,
              background:'#fff', fontSize:13, cursor:'pointer', color:'#374151' }}>
            Cancel
          </button>
          <button onClick={() => onSave(form)} disabled={saving}
            style={{ padding:'8px 22px', border:'none', borderRadius:7,
              background: saving ? '#93c5fd' : '#1a56db',
              color:'#fff', fontSize:13, fontWeight:600,
              cursor: saving ? 'not-allowed' : 'pointer' }}>
            {saving ? 'Saving…' : isEdit ? '✓ Update Employee' : '✓ Create Employee'}
          </button>
        </div>

      </div>
    </div>
  );
}


// ── Location Master Page ─────────────────────────────────────
const LOCATION_TYPES = [
  {value:'plant',    label:'Plant'},
  {value:'building', label:'Building'},
  {value:'floor',    label:'Floor'},
  {value:'bay',      label:'Bay'},
  {value:'zone',     label:'Zone'},
  {value:'room',     label:'Room'},
];

function LocationMasterPage() {
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState(null);
  const [saving,  setSaving]  = useState(false);

  const load = () => {
    setLoading(true);
    mastersAPI.listLocations()
      .then(r => setRows(r.data||[]))
      .catch(()=>{})
      .finally(()=>setLoading(false));
  };
  useEffect(load, []);

  const save = async (form) => {
    if (!form.name?.trim()) { toast.error('Location name is required'); return; }
    setSaving(true);
    try {
      const p = {
        name:      form.name.trim(),
        code:      form.code||null,
        type:      form.type||'zone',
        address:   form.address||null,
        parent_id: form.parent_id ? parseInt(form.parent_id) : null,
      };
      if (modal.id) await mastersAPI.updateLocation(modal.id, p);
      else          await mastersAPI.createLocation(p);
      toast.success(modal.id ? 'Location updated' : 'Location created');
      setModal(null);
      load();
    } catch(e) { toast.error(e?.response?.data?.detail||'Save failed'); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this location? It cannot be deleted if assets are linked.')) return;
    try { await mastersAPI.deleteLocation(id); toast.success('Deleted'); load(); }
    catch(e) { toast.error(e?.response?.data?.detail||'Delete failed'); }
  };

  /* Parent location options */
  const parentOpts = rows.map(r => ({ value: r.id, label: r.name }));

  const cols = [
    {key:'name',     label:'Location Name', bold:true},
    {key:'code',     label:'Code',          mono:true},
    {key:'type',     label:'Type',          render: r => (r.type||'zone').charAt(0).toUpperCase()+(r.type||'zone').slice(1)},
    {key:'address',  label:'Address',       render: r => r.address
      ? <span style={{fontSize:12,color:'#64748b'}}>{r.address.slice(0,40)}{r.address.length>40?'…':''}</span>
      : <span style={{color:'#cbd5e1',fontSize:11}}>—</span>},
    {key:'parent_id',label:'Parent',        render: r => {
      const p = rows.find(x => x.id === r.parent_id);
      return p ? <span style={{fontSize:12}}>{p.name}</span> : <span style={{color:'#cbd5e1',fontSize:11}}>—</span>;
    }},
  ];

  return (
    <>
      <AMasterGrid
        title="Location Master" icon="📍"
        columns={cols} rows={rows} loading={loading}
        onAdd={()=>setModal({})}
        onEdit={r=>setModal(r)}
        onDelete={del}/>

      {/* Custom modal for Location */}
      {modal && (
        <LocationModal
          modal={modal} setModal={setModal}
          parentOpts={parentOpts}
          save={save} saving={saving}/>
      )}
    </>
  );
}

function LocationModal({ modal, setModal, parentOpts, save, saving }) {
  const [form, setForm] = useState({ type:'zone', ...modal });
  const s = (k,v) => setForm(f=>({...f,[k]:v}));

  const IS = {
    width:'100%', border:'1.5px solid #e2e8f0', borderRadius:7,
    padding:'8px 11px', fontSize:13, outline:'none', boxSizing:'border-box', background:'#fff',
  };
  const LS = { fontSize:12, fontWeight:600, color:'#475569', display:'block', marginBottom:4 };

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(10,14,26,.55)',backdropFilter:'blur(4px)',
      zIndex:1200,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
      <div style={{background:'#fff',borderRadius:12,width:520,maxWidth:'96vw',maxHeight:'90vh',
        display:'flex',flexDirection:'column',boxShadow:'0 24px 64px rgba(0,0,0,.2)',overflow:'hidden'}}>

        <div style={{padding:'16px 20px 12px',borderBottom:'1px solid #e2e8f0',
          display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <div style={{fontSize:16,fontWeight:700,color:'#1a1f2e'}}>
            {modal.id ? '✏ Edit Location' : '+ Add Location'}
          </div>
          <button onClick={()=>setModal(null)}
            style={{background:'none',border:'none',cursor:'pointer',fontSize:18,color:'#94a3b8'}}>✕</button>
        </div>

        <div style={{flex:1,overflowY:'auto',padding:'16px 20px',display:'flex',flexDirection:'column',gap:14}}>

          <div style={{display:'grid',gridTemplateColumns:'2fr 1fr',gap:'12px 16px'}}>
            <div>
              <label style={LS}>Location Name *</label>
              <input style={IS} value={form.name||''} onChange={e=>s('name',e.target.value)} placeholder="e.g. Plant A, Building 2"/>
            </div>
            <div>
              <label style={LS}>Code</label>
              <input style={{...IS,fontFamily:'monospace,monospace'}} value={form.code||''} onChange={e=>s('code',e.target.value.toUpperCase())} placeholder="e.g. PLT-A"/>
            </div>
          </div>

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px 16px'}}>
            <div>
              <label style={LS}>Type</label>
              <select style={IS} value={form.type||'zone'} onChange={e=>s('type',e.target.value)}>
                {LOCATION_TYPES.map(t=><option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label style={LS}>Parent Location</label>
              <select style={IS} value={form.parent_id||''} onChange={e=>s('parent_id',e.target.value)}>
                <option value="">— None (Top Level) —</option>
                {parentOpts.filter(o=>o.value!==modal.id).map(o=>(
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label style={LS}>Address</label>
            <textarea style={{...IS,resize:'vertical',minHeight:72,fontFamily:'inherit'}}
              value={form.address||''} onChange={e=>s('address',e.target.value)}
              placeholder="Full address of this location…"/>
          </div>
        </div>

        <div style={{padding:'12px 20px',borderTop:'1px solid #e2e8f0',display:'flex',justifyContent:'flex-end',gap:10}}>
          <button onClick={()=>setModal(null)}
            style={{padding:'8px 18px',border:'1px solid #cbd5e1',borderRadius:7,background:'#fff',fontSize:13,cursor:'pointer'}}>
            Cancel
          </button>
          <button onClick={()=>save(form)} disabled={saving}
            style={{padding:'8px 20px',border:'none',borderRadius:7,
              background:saving?'#93c5fd':'#1a56db',color:'#fff',
              fontSize:13,fontWeight:600,cursor:saving?'not-allowed':'pointer'}}>
            {saving ? 'Saving…' : '✓ Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
function MethodMasterPage() {
  const [rows,setRows]=useState([]); const [loading,setLoading]=useState(true);
  const [modal,setModal]=useState(null); const [saving,setSaving]=useState(false);
  const load=()=>{ setLoading(true); mastersAPI.listDepMethods().then(r=>setRows(r.data||[])).catch(()=>{}).finally(()=>setLoading(false)); };
  useEffect(load,[]);
  const save=async(form)=>{
    if(!form.code||!form.description){toast.error('Code and Description required');return;}
    setSaving(true);
    try {
      const p={code:form.code.toUpperCase(),description:form.description,is_active:form.is_active!==false};
      if(modal.id) await mastersAPI.updateDepMethod(modal.id,p); else await mastersAPI.createDepMethod(p);
      toast.success(modal.id?'Updated':'Created'); setModal(null); load();
    } catch(e){toast.error(e?.response?.data?.detail||'Save failed');} finally{setSaving(false);}
  };
  const del=async(id)=>{ if(!window.confirm('Deactivate?'))return; try{await mastersAPI.deleteDepMethod(id);toast.success('Deactivated');load();}catch{toast.error('Failed');} };
  const cols=[{key:'code',label:'Method Code',mono:true,bold:true},{key:'description',label:'Method Description'},{key:'is_active',label:'Active',render:r=>r.is_active?'✅ Active':'❌ Inactive'}];
  const fields=[
    {key:'code',label:'Method Code',placeholder:'e.g. SL',required:true,hint:'SL = Straight Line · DB = Declining Balance · ND = No Depreciation'},
    {key:'description',label:'Method Description',placeholder:'e.g. Straight Line',required:true},
    {key:'is_active',label:'Status',type:'checkbox'},
  ];
  return (<>
    <div style={{background:'#f0f6ff',borderRadius:10,padding:'10px 16px',marginBottom:16,fontSize:13,color:'#1a56db',display:'flex',gap:8}}>
      <span style={{fontSize:16}}>ℹ</span>
      <span><strong>Default Methods:</strong> SL = Straight Line · DB = Declining Balance · ND = No Depreciation</span>
    </div>
    <AMasterGrid title="Method Master" icon="📐" columns={cols} rows={rows} loading={loading} onAdd={()=>setModal({is_active:true})} onEdit={r=>setModal(r)} onDelete={del}/>
    {modal&&<AMasterModal title={modal.id?'Edit Method':'Add Method'} fields={fields} data={modal} onSave={save} onClose={()=>setModal(null)} saving={saving}/>}
  </>);
}

// ── Depreciation Type Master Page ────────────────────────────
const CALC_METHODS = {
  SL: ['Acquisition Value / Total Useful Life','Net Book Value / Remaining Life','Percentage of Acquisition Value'],
  DB: ['Percentage'],
  ND: [],
};
function DepTypeMasterPage() {
  const [rows,setRows]=useState([]); const [methods,setMethods]=useState([]);
  const [loading,setLoading]=useState(true); const [modal,setModal]=useState(null); const [saving,setSaving]=useState(false);
  const load=()=>{ setLoading(true); Promise.all([mastersAPI.listDepTypes(),mastersAPI.listDepMethods()]).then(([rt,rm])=>{setRows(rt.data||[]);setMethods(rm.data||[]);}).catch(()=>{}).finally(()=>setLoading(false)); };
  useEffect(load,[]);
  const save=async(form)=>{
    if(!form.code||!form.description||!form.method_id){toast.error('Code, Description and Method required');return;}
    setSaving(true);
    try {
      const p={code:form.code.toUpperCase(),description:form.description,method_id:parseInt(form.method_id),calculation_base:form.calculation_base||'Y',calculation_method:form.calculation_method||null,salvage_value_pct:form.salvage_value_pct?parseFloat(form.salvage_value_pct):null,percentage:form.percentage?parseFloat(form.percentage):null,valid_from:form.valid_from||null,valid_to:form.valid_to||null,is_active:form.is_active!==false};
      if(modal.id) await mastersAPI.updateDepType(modal.id,p); else await mastersAPI.createDepType(p);
      toast.success(modal.id?'Updated':'Created'); setModal(null); load();
    } catch(e){toast.error(e?.response?.data?.detail||'Save failed');} finally{setSaving(false);}
  };
  const del=async(id)=>{ if(!window.confirm('Deactivate?'))return; try{await mastersAPI.deleteDepType(id);toast.success('Deactivated');load();}catch{toast.error('Failed');} };
  const cols=[{key:'code',label:'Code',mono:true,bold:true},{key:'description',label:'Description'},{key:'method_code',label:'Method'},{key:'calculation_base',label:'Base',render:r=>r.calculation_base==='M'?'Monthly':'Yearly'},{key:'calculation_method',label:'Calc. Method'},{key:'salvage_value_pct',label:'Salvage %',render:r=>r.salvage_value_pct!=null?`${r.salvage_value_pct}%`:'—'},{key:'percentage',label:'%',render:r=>r.percentage!=null?`${r.percentage}%`:'—'}];
  return (<>
    <div style={{background:'#f0f6ff',borderRadius:10,padding:'10px 16px',marginBottom:16,fontSize:13,color:'#1a56db',display:'flex',gap:8}}>
      <span style={{fontSize:16}}>ℹ</span>
      <span><strong>SL →</strong> shows Calculation Method options | <strong>DB →</strong> shows Percentage | <strong>ND →</strong> no depreciation fields</span>
    </div>
    <AMasterGrid title="Depreciation Type Master" icon="📊" columns={cols} rows={rows} loading={loading} onAdd={()=>setModal({is_active:true,calculation_base:'Y'})} onEdit={r=>setModal(r)} onDelete={del}/>
    {modal&&<DepTypeModal modal={modal} setModal={setModal} methods={methods} save={save} saving={saving}/>}
  </>);
}

function DepTypeModal({ modal, setModal, methods, save, saving }) {
  const [form,setForm]=useState({is_active:true,calculation_base:'Y',...modal});
  const set=(k,v)=>setForm(f=>({...f,[k]:v}));
  const methodCode=methods.find(m=>m.id===parseInt(form.method_id))?.code||'';
  const calcOpts=CALC_METHODS[methodCode]||[];
  const showPct=methodCode==='DB';
  const IS2={width:'100%',border:'1.5px solid #e2e8f0',borderRadius:7,padding:'8px 11px',fontSize:13,outline:'none',boxSizing:'border-box',background:'#fff'};
  const LS2={fontSize:12,fontWeight:600,color:'#475569',display:'block',marginBottom:4};
  const G={display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px 16px'};
  return (
    <div style={{position:'fixed',inset:0,background:'rgba(10,14,26,.55)',backdropFilter:'blur(4px)',zIndex:1200,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
      <div style={{background:'#fff',borderRadius:12,width:580,maxWidth:'96vw',maxHeight:'90vh',display:'flex',flexDirection:'column',boxShadow:'0 24px 64px rgba(0,0,0,.2)',overflow:'hidden'}}>
        <div style={{padding:'16px 20px 12px',borderBottom:'1px solid #e2e8f0',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <div style={{fontSize:16,fontWeight:700,color:'#1a1f2e'}}>{modal.id?'Edit':'Add'} Depreciation Type</div>
          <button onClick={()=>setModal(null)} style={{background:'none',border:'none',cursor:'pointer',fontSize:18,color:'#94a3b8'}}>✕</button>
        </div>
        <div style={{flex:1,overflowY:'auto',padding:'16px 20px',display:'grid',gridTemplateColumns:'1fr 1fr',gap:'14px 16px'}}>
          <div><label style={LS2}>Code *</label><input style={IS2} value={form.code||''} onChange={e=>set('code',e.target.value.toUpperCase())} placeholder="e.g. SL-10Y"/></div>
          <div style={{gridColumn:'1/-1'}}><label style={LS2}>Description *</label><input style={IS2} value={form.description||''} onChange={e=>set('description',e.target.value)} placeholder="e.g. Straight Line 10 Years"/></div>
          <div style={{gridColumn:'1/-1'}}><label style={LS2}>Method *</label>
            <select style={IS2} value={form.method_id||''} onChange={e=>set('method_id',e.target.value)}>
              <option value="">— Select Method —</option>
              {methods.map(m=><option key={m.id} value={m.id}>{m.code} — {m.description}</option>)}
            </select>
          </div>
          {methodCode&&(
            <div style={{gridColumn:'1/-1',background:'#f0f6ff',borderRadius:8,padding:'8px 12px',fontSize:12,color:'#1a56db'}}>
              <strong>Selected:</strong> {methodCode} — {methods.find(m=>m.id===parseInt(form.method_id))?.description}
              {methodCode==='ND'&&<span style={{marginLeft:8,color:'#dc2626'}}>⚠ No depreciation will be calculated</span>}
            </div>
          )}
          <div><label style={LS2}>Calculation Base</label>
            <select style={IS2} value={form.calculation_base||'Y'} onChange={e=>set('calculation_base',e.target.value)}>
              <option value="M">M — Monthly</option><option value="Y">Y — Yearly</option>
            </select>
          </div>
          <div><label style={LS2}>Salvage Value %</label><input type="number" style={IS2} value={form.salvage_value_pct||''} onChange={e=>set('salvage_value_pct',e.target.value)} placeholder="e.g. 10"/></div>
          {calcOpts.length>0&&(
            <div style={{gridColumn:'1/-1'}}>
              <label style={LS2}>Calculation Method</label>
              <select style={IS2} value={form.calculation_method||''} onChange={e=>set('calculation_method',e.target.value)}>
                <option value="">— Select —</option>
                {calcOpts.map(o=><option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          )}
          {showPct&&(
            <div><label style={LS2}>Depreciation Rate (%)</label><input type="number" style={IS2} value={form.percentage||''} onChange={e=>set('percentage',e.target.value)} placeholder="e.g. 20"/></div>
          )}
          <div><label style={LS2}>Valid From Date</label><input type="date" style={IS2} value={form.valid_from||''} onChange={e=>set('valid_from',e.target.value)}/></div>
          <div><label style={LS2}>Valid To Date</label><input type="date" style={IS2} value={form.valid_to||''} onChange={e=>set('valid_to',e.target.value)}/></div>
          <div style={{gridColumn:'1/-1',display:'flex',alignItems:'center',gap:8}}>
            <input type="checkbox" id="dt_active" checked={form.is_active!==false} onChange={e=>set('is_active',e.target.checked)} style={{width:15,height:15,cursor:'pointer'}}/>
            <label htmlFor="dt_active" style={{fontSize:13,color:'#374151',cursor:'pointer'}}>Active</label>
          </div>
        </div>
        <div style={{padding:'12px 20px',borderTop:'1px solid #e2e8f0',display:'flex',justifyContent:'flex-end',gap:10}}>
          <button onClick={()=>setModal(null)} style={{padding:'8px 18px',border:'1px solid #cbd5e1',borderRadius:7,background:'#fff',fontSize:13,cursor:'pointer'}}>Cancel</button>
          <button onClick={()=>save(form)} disabled={saving} style={{padding:'8px 20px',border:'none',borderRadius:7,background:saving?'#93c5fd':'#1a56db',color:'#fff',fontSize:13,fontWeight:600,cursor:saving?'not-allowed':'pointer'}}>{saving?'Saving…':'✓ Save'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Asset Class Master ───────────────────────────────────────
function AssetClassMasterPage() {
  const [rows, setRows]     = useState([]);
  const [loading, setLoading]= useState(true);
  const [modal, setModal]   = useState(null);
  const [saving, setSaving] = useState(false);
  const [depTypes, setDepTypes] = useState([]);

  const load = () => {
    setLoading(true);
    mastersAPI.listAssetClasses().then(r => setRows(r.data||[])).catch(()=>{}).finally(()=>setLoading(false));
  };
  useEffect(() => {
    load();
    mastersAPI.listDepTypes().then(r => setDepTypes(r.data||[])).catch(()=>{});
  }, []);

  const save = async (form) => {
    if (!form.code?.trim() || !form.description?.trim()) { toast.error('Code and Description required'); return; }
    setSaving(true);
    try {
      const p = {
        code:               form.code.trim().toUpperCase(),
        description:        form.description.trim(),
        dep_area:           form.dep_area || 'Main Books',
        dep_type_id:        form.dep_type_id ? parseInt(form.dep_type_id) : null,
        useful_life_months: form.useful_life_months ? parseInt(form.useful_life_months) : null,
        is_active:          form.is_active !== false,
      };
      if (modal.id) await mastersAPI.updateAssetClass(modal.id, p);
      else          await mastersAPI.createAssetClass(p);
      toast.success(modal.id ? 'Asset Class updated' : 'Asset Class created');
      setModal(null); load();
    } catch(e) { toast.error(e?.response?.data?.detail||'Save failed'); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Deactivate this Asset Class?')) return;
    try { await mastersAPI.deleteAssetClass(id); toast.success('Deactivated'); load(); }
    catch { toast.error('Failed'); }
  };

  const IS = {width:'100%',border:'1.5px solid #e2e8f0',borderRadius:7,padding:'8px 11px',fontSize:13,outline:'none',boxSizing:'border-box',background:'#fff'};
  const LS = {fontSize:12,fontWeight:600,color:'#475569',display:'block',marginBottom:4};

  const cols = [
    {key:'code',        label:'Code',       mono:true, bold:true},
    {key:'description', label:'Description'},
    {key:'dep_area',    label:'Dep. Area'},
    {key:'dep_type_code',label:'Dep. Type', render:r=>r.dep_type_code
      ? <span style={{fontFamily:'monospace',fontSize:12}}>{r.dep_type_code}</span>
      : <span style={{color:'#cbd5e1',fontSize:11}}>—</span>},
    {key:'useful_life_months', label:'Useful Life (Months)', render:r=>r.useful_life_months||'—'},
    {key:'is_active',   label:'Active', render:r=>r.is_active?'✅':'❌'},
  ];

  return (
    <>
      <AMasterGrid title="Asset Class Master" icon="🏷" columns={cols} rows={rows} loading={loading}
        onAdd={()=>setModal({is_active:true,dep_area:'Main Books'})}
        onEdit={r=>setModal(r)} onDelete={del}/>
      {modal && (
        <div style={{position:'fixed',inset:0,background:'rgba(10,14,26,.55)',backdropFilter:'blur(4px)',zIndex:1200,display:'flex',alignItems:'center',justifyContent:'center',padding:16}}>
          <div style={{background:'#fff',borderRadius:12,width:540,maxWidth:'96vw',maxHeight:'90vh',display:'flex',flexDirection:'column',boxShadow:'0 24px 64px rgba(0,0,0,.2)',overflow:'hidden'}}>
            <div style={{padding:'16px 20px 12px',borderBottom:'1px solid #e2e8f0',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div style={{fontSize:16,fontWeight:700,color:'#1a1f2e'}}>{modal.id?'✏ Edit Asset Class':'+ Add Asset Class'}</div>
              <button onClick={()=>setModal(null)} style={{background:'none',border:'none',cursor:'pointer',fontSize:18,color:'#94a3b8'}}>✕</button>
            </div>
            <AssetClassForm form={modal} IS={IS} LS={LS} depTypes={depTypes}
              onSave={save} onCancel={()=>setModal(null)} saving={saving}/>
          </div>
        </div>
      )}
    </>
  );
}

function AssetClassForm({ form: init, IS, LS, depTypes, onSave, onCancel, saving }) {
  const [form, setForm] = useState({is_active:true,dep_area:'Main Books',...init});
  const s = (k,v) => setForm(f=>({...f,[k]:v}));
  const G2 = {display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px 16px'};
  return (
    <>
      <div style={{flex:1,overflowY:'auto',padding:'16px 20px',display:'flex',flexDirection:'column',gap:14}}>
        <div style={G2}>
          <div><label style={LS}>Code *</label>
            <input style={{...IS,fontFamily:'monospace'}} value={form.code||''} onChange={e=>s('code',e.target.value.toUpperCase())} placeholder="e.g. MACH01"/>
          </div>
          <div><label style={LS}>Depreciation Area</label>
            <select style={IS} value={form.dep_area||'Main Books'} onChange={e=>s('dep_area',e.target.value)}>
              <option value="Main Books">Main Books</option>
              <option value="Tax Books">Tax Books</option>
            </select>
          </div>
        </div>
        <div><label style={LS}>Description *</label>
          <input style={IS} value={form.description||''} onChange={e=>s('description',e.target.value)} placeholder="e.g. Machinery and Equipment"/>
        </div>
        <div style={G2}>
          <div style={{gridColumn:'1/-1'}}><label style={LS}>Depreciation Type</label>
            <select style={IS} value={form.dep_type_id||''} onChange={e=>s('dep_type_id',e.target.value)}>
              <option value="">— Select Depreciation Type —</option>
              {depTypes.map(dt=><option key={dt.id} value={dt.id}>{dt.code} — {dt.description}</option>)}
            </select>
          </div>
          <div><label style={LS}>Useful Life (Months)</label>
            <input type="number" style={IS} value={form.useful_life_months||''} onChange={e=>s('useful_life_months',e.target.value)} placeholder="e.g. 120"/>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:8,paddingTop:20}}>
            <input type="checkbox" id="ac_active" checked={form.is_active!==false} onChange={e=>s('is_active',e.target.checked)} style={{width:15,height:15,cursor:'pointer'}}/>
            <label htmlFor="ac_active" style={{fontSize:13,color:'#374151',cursor:'pointer'}}>Active</label>
          </div>
        </div>
      </div>
      <div style={{padding:'12px 20px',borderTop:'1px solid #e2e8f0',display:'flex',justifyContent:'flex-end',gap:10}}>
        <button onClick={onCancel} style={{padding:'8px 18px',border:'1px solid #cbd5e1',borderRadius:7,background:'#fff',fontSize:13,cursor:'pointer'}}>Cancel</button>
        <button onClick={()=>onSave(form)} disabled={saving} style={{padding:'8px 20px',border:'none',borderRadius:7,background:saving?'#93c5fd':'#1a56db',color:'#fff',fontSize:13,fontWeight:600,cursor:saving?'not-allowed':'pointer'}}>
          {saving?'Saving…':'✓ Save'}
        </button>
      </div>
    </>
  );
}

// ── Warranty Period Master ───────────────────────────────────
function WarrantyPeriodMasterPage() {
  const [rows, setRows]     = useState([]);
  const [loading, setLoading]= useState(true);
  const [modal, setModal]   = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    mastersAPI.listWarrantyPeriods().then(r=>setRows(r.data||[])).catch(()=>{}).finally(()=>setLoading(false));
  };
  useEffect(load, []);

  const save = async (form) => {
    if (!form.code?.trim() || !form.warranty_name?.trim()) { toast.error('Code and Name required'); return; }
    setSaving(true);
    try {
      const p = {
        code:                form.code.toUpperCase(),
        warranty_name:       form.warranty_name,
        duration:            parseInt(form.duration)||0,
        type:                form.type||'Days',
        reminder_before_days:parseInt(form.reminder_before_days)||1,
        reminder_type:       form.reminder_type||'Year',
        is_active:           form.is_active!==false,
      };
      if (modal.id) await mastersAPI.updateWarrantyPeriod(modal.id, p);
      else          await mastersAPI.createWarrantyPeriod(p);
      toast.success(modal.id?'Updated':'Warranty Period created');
      setModal(null); load();
    } catch(e) { toast.error(e?.response?.data?.detail||'Save failed'); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Deactivate?')) return;
    try { await mastersAPI.deleteWarrantyPeriod(id); toast.success('Deactivated'); load(); }
    catch { toast.error('Failed'); }
  };

  const DTYPE = ['Days','Month','Year'];

  const cols = [
    {key:'code',         label:'Code',          mono:true, bold:true},
    {key:'warranty_name',label:'Warranty Name'},
    {key:'duration',     label:'Duration'},
    {key:'type',         label:'Type'},
    {key:'reminder_before_days', label:'Reminder Before'},
    {key:'reminder_type',label:'Reminder Type'},
    {key:'is_active',    label:'Active', render:r=>r.is_active?'✅':'❌'},
  ];

  return (
    <>
      <AMasterGrid title="Warranty Period Master" icon="🛡" columns={cols} rows={rows} loading={loading}
        onAdd={()=>setModal({is_active:true,type:'Days',reminder_type:'Year',duration:0,reminder_before_days:1})}
        onEdit={r=>setModal(r)} onDelete={del}/>
      {modal && (
        <AMasterModal
          title={modal.id?'Edit Warranty Period':'Add Warranty Period'}
          fields={[
            {key:'code',         label:'Code *',           required:true, placeholder:'e.g. WR001'},
            {key:'warranty_name',label:'Warranty Name *',  required:true, placeholder:'e.g. 1 Year Warranty'},
            {key:'duration',     label:'Duration',         type:'number', placeholder:'e.g. 12'},
            {key:'type',         label:'Type',             type:'select', options:DTYPE.map(v=>({value:v,label:v}))},
            {key:'reminder_before_days', label:'Reminder Before (days)', type:'number', placeholder:'e.g. 30'},
            {key:'reminder_type',label:'Reminder Type',   type:'select', options:DTYPE.map(v=>({value:v,label:v}))},
            {key:'is_active',    label:'Active',           type:'checkbox'},
          ]}
          data={modal} onSave={save} onClose={()=>setModal(null)} saving={saving}/>
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// ── Maintenance Type Master ──────────────────────────────────
function MaintenanceTypeMasterPage() {
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState(null);   // null=closed, obj=form data
  const [saving,  setSaving]  = useState(false);

  const BLANK = {
    code:'', name:'', description:'', is_active:true,
    is_breakdown:false, is_schedule_pm:false,
    is_inspection:false, is_repair:false,
  };

  const load = () => {
    setLoading(true);
    mastersAPI.listMaintenanceTypes()
      .then(r => setRows(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  /* ── Save (create or update) ───────────────────────────── */
  const save = async () => {
    if (!modal.code?.trim()) { toast.error('Code is required'); return; }
    if (!modal.name?.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    const payload = {
      code:          modal.code.toUpperCase().trim(),
      name:          modal.name.trim(),
      description:   modal.description || null,
      is_active:     modal.is_active !== false,
      is_breakdown:  !!modal.is_breakdown,
      is_schedule_pm:!!modal.is_schedule_pm,
      is_inspection: !!modal.is_inspection,
      is_repair:     !!modal.is_repair,
    };
    try {
      if (modal.id) await mastersAPI.updateMaintenanceType(modal.id, payload);
      else          await mastersAPI.createMaintenanceType(payload);
      toast.success(modal.id ? 'Updated successfully' : 'Created successfully');
      setModal(null);
      load();
    } catch(e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  /* ── Delete ────────────────────────────────────────────── */
  const del = async (id) => {
    if (!window.confirm('Delete this maintenance type?')) return;
    try {
      await mastersAPI.deleteMaintenanceType(id);
      toast.success('Deleted');
      load();
    } catch { toast.error('Delete failed'); }
  };

  /* ── WO type checkbox config ───────────────────────────── */
  const WO_TYPES = [
    { key:'is_breakdown',   label:'Breakdown',   color:'#dc2626', bg:'#fff1f2', border:'#fecaca' },
    { key:'is_schedule_pm', label:'Schedule PM', color:'#1a56db', bg:'#eff6ff', border:'#bfdbfe' },
    { key:'is_inspection',  label:'Inspection',  color:'#15803d', bg:'#f0fdf4', border:'#bbf7d0' },
    { key:'is_repair',      label:'Repair Work', color:'#b45309', bg:'#fefce8', border:'#fde68a' },
  ];

  const IS = { width:'100%', border:'1.5px solid #e2e8f0', borderRadius:7,
    padding:'8px 11px', fontSize:13, outline:'none', boxSizing:'border-box', background:'#fff' };
  const LS = { fontSize:12, fontWeight:600, color:'#475569',
    display:'block', marginBottom:5, textTransform:'uppercase', letterSpacing:'.04em' };

  /* ── WO Type chips for table display ──────────────────── */
  const WOChips = ({ row }) => (
    <div style={{ display:'flex', flexWrap:'wrap', gap:3 }}>
      {WO_TYPES.filter(t => row[t.key]).map(t => (
        <span key={t.key} style={{ fontSize:10, fontWeight:700,
          padding:'2px 7px', borderRadius:8,
          background:t.bg, color:t.color, border:`1px solid ${t.border}` }}>
          {t.label}
        </span>
      ))}
      {!WO_TYPES.some(t => row[t.key]) && (
        <span style={{ fontSize:11, color:'#cbd5e1' }}>—</span>
      )}
    </div>
  );

  return (
    <MasterCrudPage
      title="Maintenance Type Master" icon="🔧"
      listFn={() => mastersAPI.listMaintenanceTypes()}
      createFn={d => mastersAPI.createMaintenanceType(d)}
      updateFn={(id,d) => mastersAPI.updateMaintenanceType(id,d)}
      deleteFn={id => mastersAPI.deleteMaintenanceType(id)}
      fields={[
        { key:'code',        label:'Code *',       required:true, placeholder:'e.g. PM'            },
        { key:'name',        label:'Type Name *',  required:true, placeholder:'e.g. Preventive (PM)'},
        { key:'description', label:'Description',  type:'textarea'                                  },
        { key:'is_active',   label:'Active',       type:'checkbox'                                  },
      ]}
      columns={[
        { key:'code', label:'Code', mono:true, bold:true },
        { key:'name', label:'Maintenance Type' },
        { key:'is_active', label:'Active', render:r=>r.is_active?'✅':'❌' },
      ]}
      defaultRow={{ is_active:true }}
    />
  );
}

// ── Frequency Master ─────────────────────────────────────────
function FrequencyMasterPage() {
  return (
    <MasterCrudPage
      title="Frequency Master" icon="🔁"
      listFn={() => mastersAPI.listFrequencies()}
      createFn={d => mastersAPI.createFrequency(d)}
      updateFn={(id,d) => mastersAPI.updateFrequency(id,d)}
      deleteFn={id => mastersAPI.deleteFrequency(id)}
      fields={[
        { key:'code',          label:'Code *',             required:true, placeholder:'e.g. MONTHLY' },
        { key:'name',          label:'Frequency Name *',   required:true, placeholder:'e.g. Monthly'  },
        { key:'days_interval', label:'Days Interval',      type:'number', placeholder:'e.g. 30'       },
        { key:'is_active',     label:'Active',             type:'checkbox'                            },
      ]}
      columns={[
        { key:'code', label:'Code', mono:true, bold:true },
        { key:'name', label:'Frequency' },
        { key:'days_interval', label:'Days', render:r=>r.days_interval??'Custom' },
        { key:'is_active', label:'Active', render:r=>r.is_active?'✅':'❌' },
      ]}
      defaultRow={{ is_active:true }}
    />
  );
}

// ── Failure Type Master ──────────────────────────────────────
function FailureTypeMasterPage() {
  return (
    <MasterCrudPage
      title="Failure Type Master" icon="⚠️"
      listFn={() => mastersAPI.listFailureTypes()}
      createFn={d => mastersAPI.createFailureType(d)}
      updateFn={(id,d) => mastersAPI.updateFailureType(id,d)}
      deleteFn={id => mastersAPI.deleteFailureType(id)}
      fields={[
        { key:'code',        label:'Code *',          required:true, placeholder:'e.g. MECH'                },
        { key:'name',        label:'Failure Type *',  required:true, placeholder:'e.g. Mechanical Failure'  },
        { key:'description', label:'Description',     type:'textarea'                                       },
        { key:'is_active',   label:'Active',          type:'checkbox'                                       },
      ]}
      columns={[
        { key:'code', label:'Code', mono:true, bold:true },
        { key:'name', label:'Failure Type' },
        { key:'description', label:'Description' },
        { key:'is_active', label:'Active', render:r=>r.is_active?'✅':'❌' },
      ]}
      defaultRow={{ is_active:true }}
    />
  );
}

/* ── Generic CRUD Master Page ───────────────────────────────── */
function MasterCrudPage({
  title, icon='📋', listFn, createFn, updateFn, deleteFn,
  fields, columns, defaultRow={},
}) {
  const [rows,   setRows]   = useState([]);
  const [loading,setLoading]= useState(true);
  const [modal,  setModal]  = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    listFn().then(r=>setRows(r.data||[])).catch(()=>{}).finally(()=>setLoading(false));
  };
  useEffect(load, []);

  const save = async (form) => {
    const req = fields.find(f=>f.required && !form[f.key]?.toString().trim());
    if (req) { toast.error(`${req.label.replace(' *','')} is required`); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      fields.forEach(f => {
        if (f.type==='checkbox') payload[f.key] = form[f.key]!==false;
        if (f.type==='number' && form[f.key]==='') payload[f.key] = null;
      });
      if (modal.id) await updateFn(modal.id, payload);
      else          await createFn(payload);
      toast.success(modal.id ? 'Updated' : 'Created');
      setModal(null); load();
    } catch(e) { toast.error(e?.response?.data?.detail||'Save failed'); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Deactivate this record?')) return;
    try { await deleteFn(id); toast.success('Deactivated'); load(); }
    catch { toast.error('Failed'); }
  };

  const IS = { width:'100%', border:'1.5px solid #e2e8f0', borderRadius:7,
    padding:'8px 11px', fontSize:13, outline:'none', boxSizing:'border-box', background:'#fff' };
  const LS = { fontSize:12, fontWeight:600, color:'#475569', display:'block', marginBottom:4 };

  return (
    <>
      <AMasterGrid title={title} icon={icon} columns={columns} rows={rows} loading={loading}
        onAdd={()=>setModal({...defaultRow})} onEdit={r=>setModal({...r})} onDelete={del}/>
      {modal && (
        <div style={{ position:'fixed',inset:0,background:'rgba(10,14,26,.55)',
          backdropFilter:'blur(4px)',zIndex:1200,display:'flex',
          alignItems:'center',justifyContent:'center',padding:16 }}>
          <div style={{ background:'#fff',borderRadius:12,width:480,maxWidth:'96vw',
            maxHeight:'88vh',display:'flex',flexDirection:'column',
            boxShadow:'0 24px 64px rgba(0,0,0,.2)',overflow:'hidden' }}>
            <div style={{ padding:'14px 20px 10px',borderBottom:'1px solid #e2e8f0',
              display:'flex',justifyContent:'space-between',alignItems:'center' }}>
              <div style={{ fontSize:15,fontWeight:700,color:'#1a1f2e' }}>
                {modal.id?`✏ Edit`:`+ Add`} {title.replace(' Master','')}
              </div>
              <button onClick={()=>setModal(null)}
                style={{ background:'none',border:'none',cursor:'pointer',fontSize:18,color:'#94a3b8' }}>✕</button>
            </div>
            <div style={{ flex:1,overflowY:'auto',padding:'16px 20px',
              display:'flex',flexDirection:'column',gap:12 }}>
              {fields.map(f=>(
                <div key={f.key}>
                  <label style={LS}>{f.label}</label>
                  {f.type==='checkbox' ? (
                    <label style={{ display:'flex',alignItems:'center',gap:8,cursor:'pointer' }}>
                      <input type="checkbox" checked={modal[f.key]!==false}
                        onChange={e=>setModal(m=>({...m,[f.key]:e.target.checked}))}
                        style={{ width:15,height:15,cursor:'pointer' }}/>
                      <span style={{ fontSize:13 }}>Active</span>
                    </label>
                  ) : f.type==='textarea' ? (
                    <textarea rows={3} value={modal[f.key]||''} placeholder={f.placeholder||''}
                      onChange={e=>setModal(m=>({...m,[f.key]:e.target.value}))}
                      style={{ ...IS,resize:'vertical',fontFamily:'inherit' }}/>
                  ) : (
                    <input type={f.type||'text'} value={modal[f.key]||''}
                      placeholder={f.placeholder||''}
                      onChange={e=>setModal(m=>({...m,[f.key]:e.target.value}))}
                      style={{ ...IS,fontFamily:f.mono?'monospace':'inherit' }}/>
                  )}
                </div>
              ))}
            </div>
            <div style={{ padding:'12px 20px',borderTop:'1px solid #e2e8f0',
              display:'flex',justifyContent:'flex-end',gap:10 }}>
              <button onClick={()=>setModal(null)}
                style={{ padding:'8px 16px',border:'1px solid #cbd5e1',borderRadius:7,
                  background:'#fff',fontSize:13,cursor:'pointer' }}>Cancel</button>
              <button onClick={()=>save(modal)} disabled={saving}
                style={{ padding:'8px 20px',border:'none',borderRadius:7,
                  background:saving?'#93c5fd':'#1a56db',color:'#fff',
                  fontSize:13,fontWeight:600,cursor:saving?'not-allowed':'pointer' }}>
                {saving?'Saving…':'✓ Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// LEFT NAV CONFIG
// ─────────────────────────────────────────────────────────────
const NAV_ITEMS = [
  { key:'company',       label:'Company Master', flat:false, children:[
  /*   { key:'unit_branch',     label:'Unit / Branch'      }, */
    { key:'department',      label:'Department'          },
    { key:'functional_area', label:'Functional Area'     },
    { key:'currency',        label:'Currency'            },
  /*   { key:'event_location',  label:'Event Location'      }, */
    { key:'financial_year',  label:'Financial Year'      },
    { key:'general_setting', label:'General Setting'     },
    { key:'theme_style',     label:'Theme Style'         },
    { key:'org_levels',      label:'Organization Levels' },
  /*   { key:'dynamic_masters', label:'Dynamic Masters'     }, */
  ]},
  { key:'user_master',   label:'User Master',    flat:false, children:[
    { key:'designation', label:'Designation'        },
    { key:'role',        label:'Role'               },
    /* { key:'user_list',   label:'User List'          },
    { key:'succession',  label:'Employee Succession' }, */
  ]},
  { key:'authorization', label:'Authorization',  flat:false, children:[
/*     { key:'auth_level',  label:'Authorization Level'  }, */
    { key:'auth_matrix', label:'Authorization Matrix' },
  /*   { key:'role_perm',   label:'Role Permissions'     },
    { key:'doc_modules', label:'Document Modules'     },
    { key:'doc_type',    label:'Document Type'        }, */
  ]},
  { key:'asset_masters', label:'Asset Masters', flat:false, children:[
    { key:'location_master',          label:'Location Master'       },
    { key:'category_master',          label:'Category Master'       },
    { key:'asset_class_master',       label:'Asset Class'           },
    { key:'warranty_period_master',   label:'Warranty Period'       },
    { key:'maintenance_type_master',  label:'Maintenance Type'      },
    { key:'wo_numbering_series',      label:'WO Numbering Series'   },
    { key:'frequency_master',         label:'Frequency'             },
    { key:'failure_type_master',      label:'Failure Type'          },
    { key:'employee_master',          label:'Employee Master'       },
    { key:'method_master',            label:'Method Master'         },
    { key:'dep_type_master',          label:'Depreciation Type'     },
    { key:'email_config', label:'Email & Notifications' }

  ]},
 /*  { key:'ipartners',   label:'iPartners',        flat:true },
  { key:'ihr',         label:'iHR Masters',       flat:true },
  { key:'logs',        label:'Logs',              flat:true },
  { key:'ifeed',       label:'iFeed Masters',     flat:true },
  { key:'iqms',        label:'iQMS Masters',      flat:true },
  { key:'ipms',        label:'iPMS Masters',      flat:true },
  { key:'imemo',       label:'iMemo Masters',     flat:true },
  { key:'ileads',      label:'iLeads Masters',    flat:true },
  { key:'incidents',   label:'Incidents Masters', flat:true },
  { key:'iprojects',   label:'iProjects Master',  flat:true },
  { key:'ichange',     label:'iChange Master',    flat:true },
  { key:'iattendance', label:'iAttendance Master',flat:true },
  { key:'igates',      label:'igates Master',     flat:true },
  { key:'insight',     label:'insight Master',    flat:true },
  { key:'category',    label:'Category master',   flat:true }, */
];


/* ═══════════════════════════════════════════════════════════════
   WO NUMBERING SERIES MASTER
═══════════════════════════════════════════════════════════════ */
function WONumberingSeriesPage() {
  const API  = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(/\/$/, '');
  const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('iassets_token')}` });

  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState(null);
  const [saving,  setSaving]  = useState(false);

  const curFYStart = (() => { const n=new Date(); return n.getMonth()>=3?n.getFullYear():n.getFullYear()-1; })();
  const fmtFY = y => `FY ${y}-${String(y+1).slice(-2)}`;

  const BLANK = {
    document_type:'Work Order',
    financial_year: fmtFY(curFYStart),
    fy_start: curFYStart,
    number_start_from: parseInt(`${String(curFYStart).slice(-2)}${String(curFYStart+1).slice(-2)}00001`),
    prefix: '',
    is_active: true,
  };

  const load = () => {
    setLoading(true);
    fetch(`${API}/masters/wo-numbering-series`, { headers: hdrs() })
      .then(r => r.json()).then(d => setRows(Array.isArray(d) ? d : []))
      .catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const save = async () => {
    if (!modal.financial_year?.trim())    { toast.error('Financial Year is required'); return; }
    if (!modal.number_start_from)         { toast.error('Number Start From is required'); return; }
    setSaving(true);
    const payload = {
      document_type:    modal.document_type || 'Work Order',
      financial_year:   modal.financial_year.trim(),
      fy_start:         parseInt(modal.fy_start) || curFYStart,
      number_start_from:parseInt(modal.number_start_from),
      prefix:           modal.prefix || null,
      is_active:        modal.is_active !== false,
    };
    const url    = modal.id ? `${API}/masters/wo-numbering-series/${modal.id}` : `${API}/masters/wo-numbering-series`;
    const method = modal.id ? 'PUT' : 'POST';
    try {
      const r = await fetch(url, {
        method, headers: { ...hdrs(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error(await r.text());
      toast.success(modal.id ? 'Updated' : 'Created');
      setModal(null); load();
    } catch(e) { toast.error('Save failed: ' + e.message); }
    finally { setSaving(false); }
  };

  const del = async id => {
    if (!window.confirm('Delete this numbering series?')) return;
    try {
      await fetch(`${API}/masters/wo-numbering-series/${id}`, { method:'DELETE', headers: hdrs() });
      toast.success('Deleted'); load();
    } catch { toast.error('Delete failed'); }
  };

  const IS = { width:'100%', border:'1.5px solid #e2e8f0', borderRadius:7,
    padding:'8px 11px', fontSize:13, outline:'none', boxSizing:'border-box', background:'#fff' };
  const LS = { fontSize:12, fontWeight:600, color:'#475569', display:'block',
    marginBottom:5, textTransform:'uppercase', letterSpacing:'.04em' };
  const thSt = { padding:'10px 14px', fontSize:11, fontWeight:700, color:'#94a3b8',
    textTransform:'uppercase', letterSpacing:'.06em', borderBottom:'2px solid #f1f5f9', textAlign:'left' };
  const tdSt = { padding:'11px 14px', fontSize:13, color:'#374151', borderBottom:'1px solid #f8fafc' };

  return (
    <>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <div>
          <div style={{ fontSize:17, fontWeight:800, color:'#1a1f2e' }}>WO Numbering Series</div>
          <div style={{ fontSize:12, color:'#94a3b8', marginTop:3 }}>
            Define Work Order number sequences per Financial Year
          </div>
        </div>
        <button onClick={() => setModal({ ...BLANK })}
          style={{ padding:'9px 18px', background:'#1a56db', color:'#fff', border:'none',
            borderRadius:9, fontSize:13, fontWeight:700, cursor:'pointer' }}>
          + Add Series
        </button>
      </div>

      {/* Table */}
      <div style={{ background:'#fff', borderRadius:12, border:'1px solid #e2e8f0', overflow:'hidden' }}>
        {loading ? <div style={{ padding:40, textAlign:'center', color:'#94a3b8' }}>Loading…</div> : (
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr>
                <th style={thSt}>Document Type</th>
                <th style={thSt}>Financial Year</th>
                <th style={thSt}>Number Start From</th>
               {/*  <th style={thSt}>Current Number</th> */}
                <th style={thSt}>Prefix</th>
                <th style={thSt}>Status</th>
                <th style={{ ...thSt, textAlign:'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={7} style={{ padding:32, textAlign:'center', color:'#94a3b8' }}>
                  No numbering series found. Click "+ Add Series".
                </td></tr>
              ) : rows.map(row => (
                <tr key={row.id}>
                  <td style={{ ...tdSt, fontWeight:600 }}>{row.document_type}</td>
                  <td style={{ ...tdSt }}>
                    <span style={{ background:'#eff6ff', color:'#1a56db', fontWeight:700,
                      padding:'3px 10px', borderRadius:6, fontSize:12 }}>{row.financial_year}</span>
                  </td>
                  <td style={{ ...tdSt, fontFamily:'monospace', fontWeight:700, color:'#1a56db' }}>
                    {Number(row.number_start_from).toLocaleString('en-IN')}
                  </td>
                 {/*  <td style={{ ...tdSt, fontFamily:'monospace', fontWeight:700, color:'#16a34a' }}>
                    {Number(row.current_number).toLocaleString('en-IN')}
                  </td> */}
                  <td style={{ ...tdSt, color:'#64748b' }}>{row.prefix || '—'}</td>
                  <td style={tdSt}>
                    <span style={{ fontSize:11, fontWeight:700, padding:'3px 10px', borderRadius:10,
                      background: row.is_active ? '#f0fdf4' : '#f8fafc',
                      color: row.is_active ? '#16a34a' : '#94a3b8',
                      border: `1px solid ${row.is_active ? '#bbf7d0' : '#e2e8f0'}` }}>
                      {row.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ ...tdSt, textAlign:'right' }}>
                    <button onClick={() => setModal({ ...BLANK, ...row })}
                      style={{ padding:'5px 12px', border:'1px solid #e2e8f0', borderRadius:7,
                        background:'#fff', fontSize:12, cursor:'pointer', marginRight:6, color:'#374151' }}>
                      ✏ Edit
                    </button>
                    <button onClick={() => del(row.id)}
                      style={{ padding:'5px 12px', border:'1px solid #fecaca', borderRadius:7,
                        background:'#fff', fontSize:12, cursor:'pointer', color:'#dc2626' }}>
                      🗑
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add/Edit Modal */}
      {modal && (
        <div onClick={e => e.target === e.currentTarget && setModal(null)}
          style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.45)',
            zIndex:500, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div style={{ background:'#fff', borderRadius:14, width:480, maxWidth:'96vw',
            maxHeight:'90vh', display:'flex', flexDirection:'column',
            boxShadow:'0 24px 64px rgba(0,0,0,.2)', overflow:'hidden' }}>

            <div style={{ padding:'14px 20px 10px', borderBottom:'1px solid #e2e8f0',
              display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div style={{ fontSize:15, fontWeight:700, color:'#1a1f2e' }}>
                {modal.id ? '✏ Edit' : '+ Add'} WO Numbering Series
              </div>
              <button onClick={() => setModal(null)}
                style={{ background:'none', border:'none', cursor:'pointer', fontSize:18, color:'#94a3b8' }}>✕</button>
            </div>

            <div style={{ flex:1, overflowY:'auto', padding:'16px 20px', display:'flex', flexDirection:'column', gap:14 }}>

              {/* Document Type */}
              <div>
                <label style={LS}>Document Type</label>
                <input value={modal.document_type || 'Work Order'}
                  onChange={e => setModal(m => ({ ...m, document_type: e.target.value }))}
                  style={{ ...IS, background:'#f8fafc', color:'#64748b' }} readOnly/>
              </div>

              {/* FY Start + Financial Year label */}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div>
                  <label style={LS}>FY Start Year *</label>
                  <input type="number" value={modal.fy_start || ''}
                    min="2000" max="2100"
                    onChange={e => {
                      const y = parseInt(e.target.value) || curFYStart;
                      setModal(m => ({
                        ...m, fy_start: y,
                        financial_year: fmtFY(y),
                        number_start_from: parseInt(`${String(y).slice(-2)}${String(y+1).slice(-2)}00001`),
                      }));
                    }}
                    style={IS}/>
                </div>
                <div>
                  <label style={LS}>Financial Year</label>
                  <input value={modal.financial_year || ''} readOnly
                    style={{ ...IS, background:'#f0f6ff', color:'#1a56db', fontWeight:700 }}/>
                </div>
              </div>

              {/* Number Start From */}
              <div>
                <label style={LS}>Number Start From *</label>
                <input type="number" value={modal.number_start_from || ''}
                  onChange={e => setModal(m => ({ ...m, number_start_from: e.target.value }))}
                  placeholder="e.g. 262700001" style={{ ...IS, fontFamily:'monospace', fontSize:15, fontWeight:700 }}/>
                <div style={{ fontSize:11, color:'#94a3b8', marginTop:4 }}>
                  Convention: <strong>{fmtFY(modal.fy_start || curFYStart)}</strong> →
                  <span style={{ fontFamily:'monospace', color:'#1a56db', marginLeft:4 }}>
                    {String(modal.fy_start||curFYStart).slice(-2)}{String((modal.fy_start||curFYStart)+1).slice(-2)}NNNNN
                  </span>
                  &nbsp;(e.g. 262700001)
                </div>
              </div>

              {/* Prefix (optional) */}
              <div>
                <label style={LS}>Prefix <span style={{ fontWeight:400, color:'#94a3b8' }}>(optional)</span></label>
                <input value={modal.prefix || ''}
                  onChange={e => setModal(m => ({ ...m, prefix: e.target.value }))}
                  placeholder="Leave blank for numeric-only WO numbers"
                  style={IS}/>
              </div>

              {/* Active */}
              <label style={{ display:'flex', alignItems:'center', gap:10, cursor:'pointer' }}>
                <input type="checkbox" checked={modal.is_active !== false}
                  onChange={e => setModal(m => ({ ...m, is_active: e.target.checked }))}
                  style={{ width:15, height:15 }}/>
                <span style={{ fontSize:13 }}>{modal.is_active !== false ? 'Active' : 'Inactive'}</span>
              </label>
            </div>

            <div style={{ padding:'12px 20px', borderTop:'1px solid #e2e8f0',
              display:'flex', justifyContent:'flex-end', gap:10 }}>
              <button onClick={() => setModal(null)}
                style={{ padding:'8px 16px', border:'1px solid #cbd5e1', borderRadius:7,
                  background:'#fff', fontSize:13, cursor:'pointer' }}>Cancel</button>
              <button onClick={save} disabled={saving}
                style={{ padding:'8px 22px', border:'none', borderRadius:7,
                  background: saving ? '#93c5fd' : '#1a56db', color:'#fff',
                  fontSize:13, fontWeight:600, cursor: saving ? 'not-allowed' : 'pointer' }}>
                {saving ? 'Saving…' : (modal.id ? '💾 Update' : '+ Create')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}


// Each Company Master child key maps directly to its own page.
const PAGES = {
  // Company Master sub-pages
  unit_branch:     <UnitBranchPage/>,
  department:      <DepartmentPage/>,
  functional_area: <FunctionalAreaPage/>,
  currency:        <CurrencyPage/>,
  event_location:  <EventLocationPage/>,
  financial_year:  <FinancialYearPage/>,
  general_setting: <GeneralSettingPage/>,
  theme_style:     <ThemeStylePage/>,
  org_levels:      <OrgLevelsPage/>,
  dynamic_masters: <DynamicMastersPage/>,
  designation:     <DesignationPage />,
  role:            <RolePage />,
  user_list:       <UserListPage />,
  succession:      <SuccessionPage />,
  auth_level:      <AuthLevelPage />,
  auth_matrix:     <RolePermissionsPage />,
  role_perm:       <RolePermissionsPage />,
  doc_modules:     <ComingSoon name="Document Modules" />,
  doc_type:        <ComingSoon name="Document Type" />,
  // ── Asset Masters ──
  location_master:        <LocationMasterPage />,
  category_master:        <CategoryMasterPage />,
  asset_class_master:       <AssetClassMasterPage />,
  warranty_period_master:   <WarrantyPeriodMasterPage />,
  maintenance_type_master:  <MaintenanceTypeMasterPage />,
  wo_numbering_series:      <WONumberingSeriesPage />,
  frequency_master:         <FrequencyMasterPage />,
  failure_type_master:      <FailureTypeMasterPage />,
  employee_master:          <EmployeeMasterPage />,
  method_master:          <MethodMasterPage />,
  dep_type_master:        <DepTypeMasterPage />,

email_config: <EmailConfigPage />,
  /*ipartners:       <ComingSoon name="iPartners" />,
  ihr:             <ComingSoon name="iHR Masters" />,
  logs:            <ComingSoon name="Logs" />,
   ifeed:           <ComingSoon name="iFeed Masters" />,
  iqms:            <ComingSoon name="iQMS Masters" />,
  ipms:            <ComingSoon name="iPMS Masters" />,
  imemo:           <ComingSoon name="iMemo Masters" />,
  ileads:          <ComingSoon name="iLeads Masters" />,
  incidents:       <ComingSoon name="Incidents Masters" />,
  iprojects:       <ComingSoon name="iProjects Master" />,
  ichange:         <ComingSoon name="iChange Master" />,
  iattendance:     <ComingSoon name="iAttendance Master" />,
  igates:          <ComingSoon name="igates Master" />,
  insight:         <ComingSoon name="insight Master" />,
  category:        <ComingSoon name="Category master" />, */
};

// ─────────────────────────────────────────────────────────────
// MAIN EXPORT
// ─────────────────────────────────────────────────────────────
export default function SetupPage() {
  const { user, logout, setUser } = useAuth();
  const navigate   = useNavigate();
  const [active,   setActive]   = useState('unit_branch');
  const [groups,   setGroups]   = useState({ company:true, user_master:false, authorization:false, asset_masters:true });
  const [profile,  setProfile]  = useState(null); // null | 'view' | 'edit' | 'password'
  const [showDrop, setShowDrop] = useState(false);
  const dropRef = useRef();

  useEffect(() => {
    const h = e => { if(dropRef.current&&!dropRef.current.contains(e.target)) setShowDrop(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const initials    = user?.name?.split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase()||'U';
  const toggleGroup = key => setGroups(g=>({...g,[key]:!g[key]}));
  const hour        = new Date().getHours();
  const greeting    = hour<12?'Morning':hour<17?'Afternoon':'Evening';

  const handleLogout = async () => {
    setShowDrop(false);
    await logout();
    navigate('/login');
    toast.success('Signed out');
  };

  const handleProfileSave = updatedUser => {
    const next = {...user,...updatedUser};
    setUser(next);
    localStorage.setItem('iassets_user', JSON.stringify(next));
    setProfile(null);
  };

  // Navigate directly to clicked page key
  const handleNavClick = key => setActive(key);

  const currentPage = PAGES[active] || <ComingSoon name={active}/>;

  return (
    <div className={styles.setup_shell}>

      {/* ── LEFT NAV ── */}
      <div className={styles.setup_nav}>
        <div className={styles.setup_nav_header}>Setup</div>

        {NAV_ITEMS.map(item => {
          if (item.flat) return (
            <button
              key={item.key}
              className={`${styles.nav_flat_item} ${active===item.key?styles.nav_flat_item_active:''}`}
              onClick={()=>handleNavClick(item.key)}
            >
              <span>{item.label}</span>
              <span className={styles.nav_arrow}>›</span>
            </button>
          );

          const isOpen    = !!groups[item.key];
          const hasActive = item.children?.some(c=>c.key===active);

          return (
            <div key={item.key}>
              <button
                className={`${styles.nav_group_btn} ${hasActive?styles.nav_group_btn_active:''}`}
                onClick={()=>toggleGroup(item.key)}
              >
                <span>{item.label}</span>
                <span className={`${styles.nav_chevron} ${isOpen?styles.nav_chevron_open:''}`}>▾</span>
              </button>
              {isOpen && item.children.map(child=>(
                <button
                  key={child.key}
                  className={`${styles.nav_sub_item} ${active===child.key?styles.nav_sub_item_active:''}`}
                  onClick={()=>handleNavClick(child.key)}
                >{child.label}</button>
              ))}
            </div>
          );
        })}
      </div>

      {/* ── RIGHT CONTENT ── */}
      <div className={styles.setup_content}>

        {/* Topbar */}
         

        {/* Page content */}
        <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden'}}>
          {currentPage}
        </div>
      </div>

      {/* Profile modal */}
      {profile&&(
        <ProfileModal
          user={user}
          mode={profile}
          onClose={()=>setProfile(null)}
          onSave={handleProfileSave}
        />
      )}
    </div>
  );
}
